import { displayStringFromFHIRBoolean } from "../../datatypeComponents/BooleanEntry";
import { displayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRIdentifier } from "../../datatypeComponents/IdentifierEntry";
import { displayStringFromFHIRQuantity } from "../../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../../datatypeComponents/RangeEntry";
import { displayStringFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import getIdFromReference from "../../functions/getIdFromReference";
import generateM11Section3Composition from "./m11Section3Generator";

const narrativeStringForCharacteristic = (item) => {
    let description = item.description || "";
    if (description) {
        return description;
    }
    let code = displayStringFromFHIRCodeableConcept(item.code) || "";
    let categoryValue = "";
    if (item.valueCodeableConcept) {
        categoryValue = displayStringFromFHIRCodeableConcept(item.valueCodeableConcept);
    } else if (item.valueQuantity) {
        categoryValue = displayStringFromFHIRQuantity(item.valueQuantity);
    } else if (item.valueRange) {
        categoryValue = displayStringFromFHIRRange(item.valueRange);
    } else if (item.valueReference) {
        categoryValue = displayStringFromFHIRReference(item.valueReference);
    } else if (item.valueBoolean) {
        categoryValue = displayStringFromFHIRBoolean(item.valueBoolean);
    } else if (item.valueUri) {
        categoryValue = item.valueUri;
    } else if (item.valueExpression) {
        categoryValue = "[Defined by Expression]";
    }
    return "<b>Characteristic: </b>" + code + " matching value of " + categoryValue;
}

const generateM11ReportComposition = (researchStudyJson, resourceDictionary, extensionState) => {

    if (!researchStudyJson || !researchStudyJson.id) {
        return null;
    }
    let populationSectionSection = [
        {
            "title": "5.1 Description of Trial Population and Rationale",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218548",
                        "display": "ICH M11 Protocol Section 5.1 Description of Trial Population and Rationale"
                    }
                ],
                "text": "section5.1-population-description"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the population selected (e.g., healthy participants, adult participants, paediatric participants, pregnant participants, or breastfeeding participants) and how the enrollment criteria reflect the populations that are likely to use the drug if approved. Specify the population age range (e.g., ≤3 months, ≥18 to ≤80 years old) including the time point at which qualification for age criteria is determined (e.g., at time of screening vs randomisation for paediatric trials). Specify any key diagnostic criteria for the population (e.g., “acute lung injury”, or a specific biomarker profile). If applicable, describe similar conditions or diseases and their differential diagnosis. Provide a rationale for the trial population ensuring that the population selected is well defined and clinically recognisable. Describe how the selected population can meet the trial objectives and how the enrollment criteria reflect the population of interest. If the population targeted by a clinical question is based on a subset of the entire trial population, e.g., defined by a particular characteristic measured at baseline (e.g., a specific biomarker), this subset should be justified in this section. Justify whether the trial intervention is to be evaluated in paediatric participants, in adults unable to consent for themselves, other vulnerable participant populations, or those that may respond to the trial intervention differently (e.g., elderly, hepatic or renally impaired, or immunocompromised participants). Entries should Reference CohortDefinition (or StudyEligibilityCriteria) Profile of Group Resource.]</div>"
            }
        },
        {
            "title": "5.2 Inclusion Criteria",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218549",
                        "display": "ICH M11 Protocol Section 5.2 Inclusion Criteria"
                    }
                ],
                "text": "section5.2-inclusion-criteria"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Inclusion criteria are characteristics that define the trial population, i.e., those criteria that every potential participant must satisfy to qualify for trial enrollment. Entries should Reference CohortDefinition (or StudyEligibilityCriteria) Profile of Group Resource.]</div>"
            }
        },
        {
            "title": "5.3 Exclusion Criteria",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218550",
                        "display": "ICH M11 Protocol Section 5.3 Exclusion Criteria"
                    }
                ],
                "text": "section5.3-exclusion-criteria"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Exclusion criteria are characteristics that make an individual ineligible for participation. Entries should Reference CohortDefinition (or StudyEligibilityCriteria) Profile of Group Resource.]</div>"
            }
        },
        {
            "title": "5.4 Contraception",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218551",
                        "display": "ICH M11 Protocol Section 5.4 Contraception"
                    }
                ],
                "text": "section5.4-contraception"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
            },
            "section": [
                {
                    "title": "5.4.1 Definitions Related to Childbearing Potential",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218552",
                                "display": "ICH M11 Protocol Section 5.4.1 Definitions Related to Childbearing Potential"
                            }
                        ],
                        "text": "section5.4.1-contraception-definitions"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the definitions of: • participant of childbearing potential, and • participant of nonchildbearing potential]</div>"
                    }
                },
                {
                    "title": "5.4.2 Contraception Requirements",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218553",
                                "display": "ICH M11 Protocol Section 5.4.2 Contraception Requirements"
                            }
                        ],
                        "text": "section5.4.2-contraception-details"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the: • contraceptive methods required, and • duration of use]</div>"
                    }
                }
            ]
        },
        {
            "title": "5.5 Lifestyle Restrictions",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218554",
                        "display": "ICH M11 Protocol Section 5.5 Lifestyle Restrictions"
                    }
                ],
                "text": "section5.5-lifestyle-restrictions"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[In the following subsections, describe any restrictions during the trial pertaining to lifestyle and/or diet, intake of caffeine, alcohol, or tobacco, or physical and other activities. If not applicable, include a statement that no restrictions are required.]</div>"
            },
            "section": [
                {
                    "title": "5.5.1 Meals and Dietary Restrictions",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218555",
                                "display": "ICH M11 Protocol Section 5.5.1 Meals and Dietary Restrictions"
                            }
                        ],
                        "text": "section5.5.1-dietary-restrictions"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any restrictions on diet (for example, food and drink restrictions, timing of meals relative to dosing, etc.).]</div>"
                    }
                },
                {
                    "title": "5.5.2 Caffeine, Alcohol, Tobacco, and Other Restrictions",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218556",
                                "display": "ICH M11 Protocol Section 5.5.2 Caffeine, Alcohol, Tobacco, and Other Restrictions"
                            }
                        ],
                        "text": "section5.5.2-substances-restrictions"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any restrictions on the intake of caffeine, alcohol, tobacco, or other restrictions.]</div>"
                    }
                },
                {
                    "title": "5.5.3 Physical Activity Restrictions",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218557",
                                "display": "ICH M11 Protocol Section 5.5.3 Physical Activity Restrictions"
                            }
                        ],
                        "text": "section5.5.3-activity-restrictions"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any restrictions on activity (for example, in first-in-human trials, activity may be restricted by ensuring participants remain in bed for 4 to 6 hours after dosing).]</div>"
                    }
                },
                {
                    "title": "5.5.4 Other Activity Restrictions",
                    "code": {
                        "coding": [
                            {
                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                "code": "C218558",
                                "display": "ICH M11 Protocol Section 5.5.4 Other Activity Restrictions"
                            }
                        ],
                        "text": "section5.5.4-other-restrictions"
                    },
                    "text": {
                        "status": "empty",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe restrictions on any other activity (for example, blood or tissue donation, driving, heavy machinery use, or sun exposure).]</div>"
                    }
                }
            ]
        },
        {
            "title": "5.6 Screen Failure and Rescreening",
            "code": {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218559",
                        "display": "ICH M11 Protocol Section 5.6 Screen Failure and Rescreening"
                    }
                ],
                "text": "section5.6-screen-failure"
            },
            "text": {
                "status": "empty",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe screen failure and indicate how screen failure will be handled in the trial, including conditions and criteria upon which rescreening is acceptable. If applicable, indicate the circumstances and time window under which a repeat procedure is allowed for screen failure relating to specific inclusion/exclusion criteria for the trial.]</div>"
            }
        }
    ];

    let targetNumber;
    if (researchStudyJson?.recruitment?.targetNumber) {
        targetNumber = researchStudyJson.recruitment.targetNumber;
    }
    let isRandomized = false;
    let blindedRolesList = [];
    if (researchStudyJson?.studyDesign?.length) {
        for (const studyDesign of researchStudyJson.studyDesign) {
            if (studyDesign?.coding?.[0]?.code === "SEVCO:01003") {
                isRandomized = true;
            }
            if (studyDesign?.coding?.[0]?.code === "SEVCO:01060") {
                blindedRolesList.push("Study Participants");
            }
            if (studyDesign?.coding?.[0]?.code === "SEVCO:01061") {
                blindedRolesList.push("Intervention Providers");
            }
            if (studyDesign?.coding?.[0]?.code === "SEVCO:01062") {
                blindedRolesList.push("Outcome Assessors");
            }
            if (studyDesign?.coding?.[0]?.code === "SEVCO:01063") {
                blindedRolesList.push("Data Analysts");
            }
        }
    }

    let numberOfParticipantsStatement = "[State the expected number of participants to be assigned to trial intervention/enrolled.  Indicate whether the number provided is the target or maximum number of individuals to be randomly assigned to trial intervention/enrolled. State as 'A [Target/Maximum] of [Enter Number of Participants] participants will be [randomly assigned to trial intervention/enrolled].]";
    let numberOfParticipantsSectionText = {
        "status": "empty",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + numberOfParticipantsStatement + "</div>"
    };
    if (targetNumber) {
        if (isRandomized) {
            numberOfParticipantsStatement = "A Target of " + String(targetNumber) + " participants will be randomly assigned to trial intervention.";
        } else {
            numberOfParticipantsStatement = "A Target of " + String(targetNumber) + " participants will be enrolled.";
        }
        numberOfParticipantsSectionText = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + numberOfParticipantsStatement + "</div>"
        };
    }

    let blindedRolesStatement = "[State 'The following roles indicated will not be made aware of the treatment group assignment during the trial:' [Blinded Roles] --OR-- 'Not applicable (No blinding)' indicates an open-label trial. For [Blinded Roles] enter any of the following which apply: Participant (C142710); Care Provider (C17445); Investigator (C25936); Outcomes Assessor (C207599); Sponsor (C70793); Not Applicable (C48660).]";
    let blindedRolesSectionText = {
        "status": "empty",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + blindedRolesStatement + "</div>"
    };
    if (blindedRolesList.length > 0) {
        blindedRolesStatement = blindedRolesList.join(", ") + ".  REPLACE this list with any of the following that apply: For [Blinded Roles] enter any of the following which apply: Participant (C142710); Care Provider (C17445); Investigator (C25936); Outcomes Assessor (C207599); Sponsor (C70793).";
        blindedRolesSectionText = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + blindedRolesStatement + "</div>"
        };
    }

    let numberOfArmsStatement = "[Enter the numeric value for the number of arms in the trial. For trials with a different number of arms in different periods, populate this field based on the total number of arms.]";
    let numberOfArmsSectionText = {
        "status": "empty",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + numberOfArmsStatement + "</div>"
    };
    if (researchStudyJson?.comparisonGroup?.length > 0) {
        numberOfArmsStatement = String(researchStudyJson.comparisonGroup.length);
        numberOfArmsSectionText = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + numberOfArmsStatement + "</div>"
        };
    }

    if (researchStudyJson?.recruitment?.eligibility) {
        let theFoi = getIdFromReference(researchStudyJson.recruitment.eligibility);
        if (!isNaN(theFoi)) {
            let theResource = resourceDictionary[theFoi];
            if (theResource?.description) {
                populationSectionSection[0].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the population selected (e.g., healthy participants, adult participants, paediatric participants, pregnant participants, or breastfeeding participants) and how the enrollment criteria reflect the populations that are likely to use the drug if approved. Specify the population age range (e.g., ≤3 months, ≥18 to ≤80 years old) including the time point at which qualification for age criteria is determined (e.g., at time of screening vs randomisation for paediatric trials). Specify any key diagnostic criteria for the population (e.g., “acute lung injury”, or a specific biomarker profile). If applicable, describe similar conditions or diseases and their differential diagnosis. Provide a rationale for the trial population ensuring that the population selected is well defined and clinically recognisable. Describe how the selected population can meet the trial objectives and how the enrollment criteria reflect the population of interest. If the population targeted by a clinical question is based on a subset of the entire trial population, e.g., defined by a particular characteristic measured at baseline (e.g., a specific biomarker), this subset should be justified in this section. Justify whether the trial intervention is to be evaluated in paediatric participants, in adults unable to consent for themselves, other vulnerable participant populations, or those that may respond to the trial intervention differently (e.g., elderly, hepatic or renally impaired, or immunocompromised participants).]<p>---------------------------------</p>" + theResource.description + "</div>"
                };
                populationSectionSection[0].entry = [researchStudyJson.recruitment.eligibility];
            }
            if (theResource?.characteristic?.length > 0) {
                let inclusionCriteria = [];
                let exclusionCriteria = [];
                for (const criteria of theResource.characteristic) {
                    if (criteria.exclude === true && (criteria.description || criteria.code)) {
                        exclusionCriteria.push(criteria);
                    }
                    if (criteria.exclude === false && (criteria.description || criteria.code)) {
                        inclusionCriteria.push(criteria);
                    }
                }
                if (inclusionCriteria.length > 0) {
                    let inclusionCriteriaStrings = inclusionCriteria.map(item => narrativeStringForCharacteristic(item)); //TODO - check if this line needs a return to function 
                    let narrativeSummaryString = "<p>To be eligible to participate in this trial, an individual must meet all the following criteria:</p><p>" + inclusionCriteriaStrings
                        .map((item, itemIndex) => {
                            let index = itemIndex + 1;
                            return String(index) + ". " + item + "</p>";
                        })
                        .join("<p>") + "</p>";
                    populationSectionSection[1].text = {
                        "status": "generated",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + narrativeSummaryString + "</div>"
                    };
                    populationSectionSection[1].entry = [researchStudyJson.recruitment.eligibility];
                    populationSectionSection[2].entry = [researchStudyJson.recruitment.eligibility];
                    if (exclusionCriteria.length > 0) {
                        let exclusionCriteriaStrings = exclusionCriteria.map(item => narrativeStringForCharacteristic(item)); //TODO - check if this line needs a return to function 
                        let narrativeSummaryString2 = "<p>An individual who meets any of the following criteria will be excluded from participation in this trial:</p><p>" + exclusionCriteriaStrings
                            .map((item, itemIndex) => {
                                let index = itemIndex + 1;
                                return String(index) + ". " + item + "</p>";
                            })
                            .join("<p>") + "</p>";
                        populationSectionSection[2].text = {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + narrativeSummaryString2 + "</div>"
                        };

                    } else {
                        populationSectionSection[2].text = {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>No exclusion criteria.</p></div>"
                        };
                    }
                }
            }
        }
    }

    let sponsorConfidentialityStatement = extensionState?.confidentialityStatement;
    let acronym;
    let shortTitle;
    let version = researchStudyJson.version || "";
    let versionDate = researchStudyJson.date || "";
    if (researchStudyJson.label?.length > 0) {
        for (const label of researchStudyJson.label) {
            let type = label?.type?.coding?.[0]?.code;
            if (type === "acronymn" && label.value && !acronym) {
                acronym = label.value;
            }
            if ((type === "plain-language" || type === "short-title") && label.value && !shortTitle) {
                shortTitle = label.value;
            }
        }
    }
    let sponsorProtocolIdentifier = "[Replace with a unique alphanumeric identifier for the trial, designated by the sponsor.]";
    let identifierList = "";
    if (researchStudyJson.identifier?.length > 0) {
        for (const identifier of researchStudyJson.identifier) {
            if (identifier.type?.text === "Sponsor Protocol Identifier" && identifier.value) {
                sponsorProtocolIdentifier = identifier.value;
            } else {
                identifierList += "<p>" + displayStringFromFHIRIdentifier(identifier) + "</p>";
            }
        }
    }
    let trialPhase = "[For trials combining investigational drugs or vaccines with devices, classify according to the phase of drug development.]";
    if (researchStudyJson.phase?.coding?.[0]?.display || researchStudyJson.phase?.text) {
        trialPhase = researchStudyJson.phase.coding?.[0]?.display || researchStudyJson.phase.text;
    }
    let titleNarrative = "<table>";
    if (sponsorConfidentialityStatement) {
        titleNarrative += "<tr><td><strong>Sponsor Confidentiality Statement:</strong></td><td>" + sponsorConfidentialityStatement + "</td></tr>";
    }
    titleNarrative += "<tr><td><strong>Full Title:</strong></td><td>" + (researchStudyJson.title?.replace(" - M11ResearchStudy", "") || researchStudyJson.name) + "</td></tr>";
    if (acronym) {
        titleNarrative += "<tr><td><strong>Trial Acronym:</strong></td><td>" + acronym + "</td></tr>";
    }
    titleNarrative += "<tr><td><strong>Sponsor Protocol Identifier:</strong></td><td>" + sponsorProtocolIdentifier + "</td></tr>";
    titleNarrative += "<tr><td><strong>Original Protocol:</strong></td><td>[Enter Yes (C49488) or No (C49487)]</td></tr>";
    titleNarrative += "<tr><td><strong>Version Number:</strong></td><td>" + (version || "[For use by the sponsor at their discretion.]") + "</td></tr>";
    titleNarrative += "<tr><td><strong>Version Date:</strong></td><td>" + (versionDate || "[For use by the sponsor at their discretion.]") + "</td></tr>";
    titleNarrative += "<tr><td><strong>Amendment Identifier:</strong></td><td>[Enter the amendment identifier. If this is the original instance of the protocol, delete this row or enter 'Not applicable'.]</td></tr>";
    titleNarrative += "<tr><td><strong>Amendment Scope:</strong></td><td>[If this is the original instance of the protocol, delete this row or enter 'Not applicable'. If the amendment applies to all sites in the trial or amending a single-country trial, enter 'Global'. For an amendment that does not apply to all sites in the trial, enter 'Not Global' and enter a Country Identifier, Region Identifier, or Site Identifier based on amendment scope.]</td></tr>";
    titleNarrative += "<tr><td><strong>Sponsor’s Investigational Product Code(s):</strong></td><td>[Enter the sponsor’s unique identifier for investigational product(s) in the trial. Add fields as needed.]</td></tr>";
    titleNarrative += "<tr><td><strong>Investigational Product Name(s):</strong></td><td>[Omit nonproprietary name field if a nonproprietary name has not yet been assigned. Omit proprietary name field if not yet established.]</td></tr>";
    titleNarrative += "<tr><td><strong>Trial Phase:</strong></td><td>" + trialPhase + "</td></tr>";
    if (shortTitle) {
        titleNarrative += "<tr><td><strong>Short Title:</strong></td><td>" + shortTitle + "</td></tr>";
    }
    titleNarrative += "<tr><td><strong>Sponsor Name and Address:</strong></td><td><p>[Enter Sponsor Name.]</p><p>[Enter Sponsor Legal Address.]</p><p>[Add headings and lines for each Co-Sponsor Name and Address: and Local Sponsor Name and Address: if needed.]</p></td></tr>";
    titleNarrative += "<tr><td><strong>Device Manufacturer Name and Address:</strong></td><td>[Manufacturer name and address information is required only for drug / device combination protocols that include investigational device(s) and should not be included for other protocols. Include the manufacturer address only if the manufacturer is different than the sponsor listed above. Add additional fields as needed if multiple investigational devices will be used in the trial or if there are multiple manufacturers for a single device. Delete this line if not applicable.]</td></tr>";
    titleNarrative += "<tr><td><strong>Regulatory or Clinical Trial Identifier(s):</strong></td><td>" + identifierList + "</td></tr>";
    titleNarrative += "<tr><td><strong>Sponsor Approval:</strong></td><td>[Enter Sponsor Approval Date or State the location where sponsor approval information can be found]</td></tr>";
    titleNarrative += "</table>";
    titleNarrative += "<p><strong>Sponsor Signatory:</strong> [Enter sponsor signatory (e.g., name, title, signature and date, or State the location where sponsor signatory information can be found (e.g., electronic signature)]</p>";
    titleNarrative += "<p><strong>Medical Expert Contact:</strong> [Enter Medical Expert contact information(as designated by sponsor, or State the location where Medical Expert contact information can be found]</p>";
    titleNarrative += "<p><strong>Amendment Details</strong></p>";
    titleNarrative += "<p>[For an original protocol that has not been amended, state 'This protocol has not been amended.' Otherwise state 'This is the first protocol amendment.' OR 'This protocol has been amended previously. Details of prior amendments are presented in Section 12.3 Prior Protocol Amendment(s).']</p>";
    titleNarrative += "<p>[Delete the tables below if there is no current amendment.]</p>";
    titleNarrative += "<table>";
    titleNarrative += "<tr><td><strong>Approximate [#]% Enrolled at Time of Sponsor Approval:</strong></td><td>Approximately [#]% enrolled [Choose one of: Globally, Locally, or By Cohort]</td></tr>";
    titleNarrative += "<tr><td><strong>Reason(s) for Amendment:</strong></td>";
    titleNarrative += "<td>Primary: [Choose from: Regulatory Agency Request To Amend (C218490), New Regulatory Guidance (C218491), IRB/IEC Feedback (C218492), New Safety Information Available (C218493), Manufacturing Change (C218494), IMP Addition (C218495), Change In Strategy (C218496), Change In Standard Of Care (C218497), New Data Available (Other Than Safety Data) (C218498), Investigator/Site Feedback (C218499), Recruitment Difficulty (C218500), Inconsistency And/Or Error In The Protocol (C218501), Protocol Design Error (C218502), Other (C17649), Not Applicable (C48660)]</td>";
    titleNarrative += "<td>Secondary: [Choose from: Regulatory Agency Request To Amend (C218490), New Regulatory Guidance (C218491), IRB/IEC Feedback (C218492), New Safety Information Available (C218493), Manufacturing Change (C218494), IMP Addition (C218495), Change In Strategy (C218496), Change In Standard Of Care (C218497), New Data Available (Other Than Safety Data) (C218498), Investigator/Site Feedback (C218499), Recruitment Difficulty (C218500), Inconsistency And/Or Error In The Protocol (C218501), Protocol Design Error (C218502), Other (C17649), Not Applicable (C48660)]</td>";
    titleNarrative += "</tr>";
    titleNarrative += "<tr><td><strong>Amendment Summary:</strong></td><td>[Briefly describe key changes.]</td></tr>";
    titleNarrative += "<tr><td>Is this amendment likely to have a substantial impact on the safety or rights of the participants?</td><td>[State 'Yes' or 'No'. If yes, briefly explain.]</td></tr>";
    titleNarrative += "<tr><td>Is this amendment likely to have a substantial impact on the reliability and robustness of the data generated in the clinical trial?</td><td>[State 'Yes' or 'No'. If yes, briefly explain.]</td></tr>";
    titleNarrative += "</table>";
    titleNarrative += "<p><strong>Overview of Changes in the Current Amendment</strong></p>";
    titleNarrative += "<table>";
    titleNarrative += "<tr><td><strong>Description of Change</strong></td>";
    titleNarrative += "<td><strong>Brief Rationale for Change</strong></td>";
    titleNarrative += "<td><strong>Section # and Name</strong></td></tr>";
    titleNarrative += "<tr><td>[Enter Description of Change]</td>";
    titleNarrative += "<td>[Enter Brief Rationale for Change]</td>";
    titleNarrative += "<td>[Enter Section # and Name]</td></tr>";
    titleNarrative += "</table>";

    let m11ReportContainerJson = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-container"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report for " + (researchStudyJson.title?.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "Title Page",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C222769",
                            "display": "ICH M11 Protocol Section Title Page"
                        }
                    ],
                    "text": "section0-title-page"
                },
                "text": {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + titleNarrative + "</div>"
                }
            },
            {
                "title": "1 Protocol Summary",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218514",
                            "display": "ICH M11 Protocol Section 1 PROTOCOL SUMMARY"
                        }
                    ],
                    "text": "section1-protocol-summary"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection01 Profile).]</div>"
                }
            },
            {
                "title": "2 Introduction",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218520",
                            "display": "ICH M11 Protocol Section 2 INTRODUCTION"
                        }
                    ],
                    "text": "section2-introduction"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection02 Profile).]</div>"
                }
            },
            {
                "title": "3 Trial Objectives and Estimands",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218526",
                            "display": "ICH M11 Protocol Section 3 TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS"
                        }
                    ],
                    "text": "section3-estimands"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection03 Profile).]</div>"
                }
            },
            {
                "title": "4 Trial Design",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218533",
                            "display": "ICH M11 Protocol Section 4 TRIAL DESIGN"
                        }
                    ],
                    "text": "section4-trial-design"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection04 Profile).]</div>"
                }
            },
            {
                "title": "5 Trial Population",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218547",
                            "display": "ICH M11 Protocol Section 5 TRIAL POPULATION"
                        }
                    ],
                    "text": "section5-trial-population"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection05 Profile).]</div>"
                }
            },
            {
                "title": "6 Trial Intervention",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218560",
                            "display": "ICH M11 Protocol Section 6 TRIAL INTERVENTION AND CONCOMITANT THERAPY"
                        }
                    ],
                    "text": "section6-trial-intervention"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection06 Profile).]</div>"
                }
            },
            {
                "title": "7 Participant Discontinuation",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218583",
                            "display": "ICH M11 Protocol Section 7 PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL"
                        }
                    ],
                    "text": "section7-participant-discontinuation"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection07 Profile).]</div>"
                }
            },
            {
                "title": "8 Trial Assessments",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218590",
                            "display": "ICH M11 Protocol Section 8 TRIAL ASSESSMENTS AND PROCEDURES"
                        }
                    ],
                    "text": "section8-assessments"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection08 Profile).]</div>"
                }
            },
            {
                "title": "9 Adverse Events",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218608",
                            "display": "ICH M11 Protocol Section 9 ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS"
                        }
                    ],
                    "text": "section9-adverse-events"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection09 Profile).]</div>"
                }
            },
            {
                "title": "10 Statistical Considerations",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218625",
                            "display": "ICH M11 Protocol Section 10 STATISTICAL CONSIDERATIONS"
                        }
                    ],
                    "text": "section10-statistics"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection10 Profile).]</div>"
                }
            },
            {
                "title": "11 Trial Oversight",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218649",
                            "display": "ICH M11 Protocol Section 11 TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS"
                        }
                    ],
                    "text": "section11-oversight"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection11 Profile).]</div>"
                }
            },
            {
                "title": "12 Supporting Details",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218666",
                            "display": "ICH M11 Protocol Section 12 APPENDIX: SUPPORTING DETAILS"
                        }
                    ],
                    "text": "section12-supporting-details"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection12 Profile).]</div>"
                }
            },
            {
                "title": "13 Glossary",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218670",
                            "display": "ICH M11 Protocol Section 13 APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS"
                        }
                    ],
                    "text": "section13-glossary"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection13 Profile).]</div>"
                }
            },
            {
                "title": "14 References",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218671",
                            "display": "ICH M11 Protocol Section 14 APPENDIX: REFERENCES"
                        }
                    ],
                    "text": "section14-references"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Entry should Reference Composition Resource (M11ReportSection14 Profile).]</div>"
                }
            }
        ]
    };
    let m11ReportSection01Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-01"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218514",
                        "display": "ICH M11 Protocol Section 1 PROTOCOL SUMMARY"
                    }
                ],
                "text": "section1-protocol-summary"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 1 (Protocol Summary) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "1 PROTOCOL SUMMARY",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218514",
                            "display": "ICH M11 Protocol Section 1 PROTOCOL SUMMARY"
                        }
                    ],
                    "text": "section1-protocol-summary"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "1.1 Protocol Synopsis",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218515",
                                    "display": "ICH M11 Protocol Section 1.1 Protocol Synopsis"
                                }
                            ],
                            "text": "section1.1-protocol-synopsis"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "1.1.1 Primary and Secondary Objectives and Estimands",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218516",
                                            "display": "ICH M11 Protocol Section 1.1.1 Primary and Secondary Objectives and Estimands"
                                        }
                                    ],
                                    "text": "section1.1.1-objectives-estimands"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Summarise the primary and secondary objectives and any associated estimands in natural, nontechnical (layperson) language. For trials intended to estimate a treatment effect or test a hypothesis related to a treatment effect, include the primary and secondary objectives and any associated estimands using a nontechnical summary describing the objective and treatment effect of interest (estimand). For other types of trials not intended to estimate a treatment effect or test a hypothesis related to a treatment effect, define trial objectives and describe additional information relevant to the clinical question(s) of interest (e.g., the endpoint(s) associated with each objective). For trials with numerous objectives in which the description of objectives will exceed half a page, consider including the most important objectives and estimands in the synopsis and refer to Section 3 Trial Objectives and Associated Estimands, which covers the objectives and estimands in technical detail. For considerations on estimands, refer to ICH E9(R1).]</div>"
                                }
                            },
                            {
                                "title": "1.1.2 Overall Design",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218517",
                                            "display": "ICH M11 Protocol Section 1.1.2 Overall Design"
                                        }
                                    ],
                                    "text": "section1.1.2-overall-design"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Key aspects of the trial design are summarised below.</p><table><tbody><tr><td><strong>Intervention:</strong> [Enter Sponsor's Invesigational Product Code(s) and/or Nonproprietary Name(s)]</td><td><strong>Population Type:</strong> [Enter Population Type with one of: With Disease (C218503); Without Disease (C218504)]</td></tr><tr><td><strong>Intervention Model:</strong> [Enter Intervention Model with one of: Single Group (C82640); Parallel Group (C82639); Cross-over (C82637); Factorial (C82638); Sequential (C142568); Other (C17649)]</td><td><strong>Population Diagnosis or Condition:</strong> [Enter Population Diagnosis or Condition, using MedDRA Preferred Term(s) or indicate 'other' and describe.]</td></tr><tr><td><strong>Control Type:</strong> [Enter Control Type with one of: Placebo (C49648); Active Comparator (C49649); Dose Response (C120841); Different Dose or Regimen (C218505); External (C218506); Sham Procedure (C184727); No Control (C28280)]</td><td><p><strong>Population Age:</strong> Minimum: [Enter Minimum Age with Units of Age in Hours, Days, Weeks, Months, or Years]</p><p>Maximum: [Enter Maximum Age with Units of Age in Hours, Days, Weeks, Months, or Years]</p></td></tr><tr><td><strong>Control Description:</strong> [Enter Nonproprietary Name, or International Nonproprietary Name, or 'Not applicable']</td><td><strong>Site Distribution and Geographic Scope:</strong> [Enter Site Distribution with one of: Single-Centre (C217004), Multicentre (C217005) AND Enter Site Geographic Scope with one of: Single Country (C217006); Multiple Countries (C217007)]</td></tr><tr><td><p><strong>Intervention Assignment Method:</strong> [Enter Intervention Assignment Method with one of: Randomisation (C25196); No Intervention Assignment Method (C222801); Other (C17649)]</p><p>[If Intervention Assignment Method is 'Randomisation', then enter Randomisation Type (e.g., simple randomisation, block randomisation). If Intervention Assignment Method is 'Other', then enter Other Intervention Assignment Method]</p><p><strong>Stratification</strong> [Enter Stratification Indicator with one of: Yes (C49488), No (C49487)]</p></td><td><strong>Master Protocol:</strong> [Enter Master Protocol Indicator with one of: Yes (C49488), No (C49487)]</td></tr><tr><td><strong>Drug/Device Combination Product Indicator:</strong> [Enter Drug/Device Combination Product Indicator with one of: Yes (C49488), No (C49487)]</td><td><strong>Adaptive Trial Design:</strong> [Enter Adaptive Trial Design Indicator with one of: Yes (C49488), No (C49487)]</td></tr></tbody></table></div>"
                                }
                            },
                            {
                                "title": "Number of Arms:",
                                "code": {
                                    "text": "Number of Arms"
                                },
                                "text": numberOfArmsSectionText
                            },
                            {
                                "title": "Trial Blind Schema:",
                                "code": {
                                    "text": "Trial Blind Schema"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[For designs in which these details may differ in one or more trial periods, answer according to the portion of the trial in which the highest number of blinded roles occurs. Additional details can be provided in Section 6.7.3 Measures to Maintain Blinding. For the content here, enter one of the following values: Double Blind (C15228), Observer Blind (C187674), Open Label (C49659), Single Blind (C28233).]</div>"
                                }
                            },
                            {
                                "title": "Blinded roles: The following roles indicated will not be made aware of the treatment group assignment during the trial:",
                                "code": {
                                    "text": "Blinded Roles"
                                },
                                "text": blindedRolesSectionText
                            },
                            {
                                "title": "Number of Participants:",
                                "code": {
                                    "text": "Number of Participants"
                                },
                                "text": numberOfParticipantsSectionText
                            },
                            {
                                "title": "Duration:",
                                "code": {
                                    "text": "Duration"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Start with either 'Total planned duration of trial intervention for each participant:' or 'Total planned duration of trial participation for each participant:'.  Note that the total duration of trial participation should include any washout and any follow-up periods in which the participant is not receiving trial intervention.  When duration will vary, provide a short explanation (for example, “event-driven” or “adaptive design”. If necessary, include any clarifications or cross references to details in the main body of the protocol.]</div>"
                                }
                            },
                            {
                                "title": "Committees:",
                                "code": {
                                    "text": "Committees"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>[Use the lines below to Indicate whether any committee(s) will be reviewing data while the trial is ongoing, and the type of committee.  Common examples include Data Monitoring Committee, Dose Escalation Committee, or Endpoint Adjudication Committee; describe others, if applicable.   List independent committees in the space indicated.  Other committees may be included at the Sponsor’s discretion in the separate space provided.  Committees listed here should be fully described in Section 11.4 Committees.]</p><p>Independent Committees: [Enter Independent Committees here.]</p><p>Other Committees: [Enter Other Committees here. Delete 'Other Committees:' if not applicable.]</p></div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "1.2 Trial Schema",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218518",
                                    "display": "ICH M11 Protocol Section 1.2 Trial Schema"
                                }
                            ],
                            "text": "section1.2-trial-schema"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[The purpose of this section is to provide a visual depiction of the trial design, orienting users of the protocol to the key features of the design. The schema depicts the trial arms, the flow of individual participant through the progression of trial period(s)/epochs (such as screening, washout/run-in, intervention, and key milestones [e.g., randomisation, cross-over, end of treatment, end of study, post-treatment follow-up]). For complex trials, additional schemas may be added to describe activities or trial periods in greater detail. Entries should Reference SoaPlanDefinition Profile of PlanDefinition Resource.]</div>"
                        }
                    },
                    {
                        "title": "1.3 Schedule of Activities",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218519",
                                    "display": "ICH M11 Protocol Section 1.3 Schedule of Activities"
                                }
                            ],
                            "text": "section1.3-schedule-of-activities"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[The schedule of activities must capture the procedures that will be accomplished at each trial visit, and all contact with participants, for example, telephone contacts. This includes any tests that are used for eligibility, participant randomisation or stratification, or decisions on trial intervention discontinuation. Allowable windows should be stated for all visits and procedures. A tabular format is recommended. When applicable for studies with extensive sampling (e.g., serial PK sampling) a separate table may be added. Entries should Reference SoaPlanDefinition Profile of PlanDefinition Resource.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection02Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-02"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218520",
                        "display": "ICH M11 Protocol Section 2 INTRODUCTION"
                    }
                ],
                "text": "section2-introduction"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 2 (Introduction) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "2 INTRODUCTION",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218520",
                            "display": "ICH M11 Protocol Section 2 INTRODUCTION"
                        }
                    ],
                    "text": "section2-introduction"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "2.1 Purpose of Trial",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218521",
                                    "display": "ICH M11 Protocol Section 2.1 Purpose of Trial"
                                }
                            ],
                            "text": "section2.1-trial-purpose"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Explain why the trial is needed, and why the research questions being asked are important. Do not restate the objectives or estimands. Do not restate the IB; rather, cross reference to the IB as applicable to the description.]</div>"
                        }
                    },
                    {
                        "title": "2.2 Assessment of Risks and Benefits",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218522",
                                    "display": "ICH M11 Protocol Section 2.2 Assessment of Risks and Benefits"
                                }
                            ],
                            "text": "section2.2-benefits-risks"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include an assessment of known and potential risks and benefits, if any, as a result of participating in the trial from the perspective of an individual participant, including the basis of the risk (e.g., nonclinical trials or prior clinical trials). This section may be structured under one single heading 2.2 Assessment of Risks and Benefits, or if applicable under 3 subheadings as 2.2.1 Risk Summary and Mitigation Strategy, 2.2.2 Benefit Assessment and 2.2.3 Overall Risk-Benefit Assessment]</div>"
                        },
                        "section": [
                            {
                                "title": "2.2.1 Risk Summary and Mitigation Strategy",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218523",
                                            "display": "ICH M11 Protocol Section 2.2.1 Risk Summary and Mitigation Strategy"
                                        }
                                    ],
                                    "text": "section2.2.1-risk-summary"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[You may enter the entire risk summary here or use the subsections for Trial Intervention, Trial Procedures, and Other as noted below.]</div>"
                                },
                                "section": [
                                    {
                                        "title": "Trial-specific Intervention Risks and Mitigations",
                                        "code": {
                                            "text": "section2.2.1.1-trial-intervention-risk-summary"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Trial Intervention – Describe risks related to trial-specific treatments and interventions.  For the protocol, focus only on the relevant key risks for THIS trial.  Provide a brief description of strategies to mitigate identified risks or provide a cross-reference to the relevant protocol section.]</div>"
                                        }
                                    },
                                    {
                                        "title": "Trial-specific Procedure Risks and Mitigations",
                                        "code": {
                                            "text": "section2.2.1.2-trial-procedures-risk-summary"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Trial Procedures – Describe risks associated with the design (e.g., placebo arm) and procedures specific to this trial (e.g., biopsies), and any measures to control or mitigate the risks. Provide a brief description of strategies to mitigate identified risks or provide a cross reference to the relevant protocol section. This is not intended to be an exhaustive list of all possible risks associated with trial procedures but should focus on the unique risks inherent in the design or less common or high-risk procedures. As above, provide a brief description of strategies to mitigate identified risks or provide a cross reference to the relevant protocol section.]</div>"
                                        }
                                    },
                                    {
                                        "title": "Trial-specific Other Risks and Mitigations",
                                        "code": {
                                            "text": "section2.2.1.3-trial-other-risk-summary"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Other – Consider risks associated with other items (for example, comparators, challenge agents, imaging agents, medical devices). This could include discussion of risk mitigation for special populations, if not described elsewhere. Insert a line for each, as needed.]</div>"
                                        }
                                    }
                                ]
                            },
                            {
                                "title": "2.2.2 Benefit Summary",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218524",
                                            "display": "ICH M11 Protocol Section 2.2.2 Benefit Summary"
                                        }
                                    ],
                                    "text": "section2.2.2-benefit-summary"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[The benefit summary should describe any physical, psychological, social, or any other potential benefits to individual participants as a result of participating in the trial, addressing immediate potential benefits and/or long-range potential benefits. Clearly state if no benefits to an individual participant can be anticipated, or if potential benefits are unknown. For early clinical trials such as Phase 1 or trials in healthy participants, benefits for an individual participant (other than those of altruism) are expected to be minimal. Benefits to society in general may also be included but should be described separately from the individual participant perspective.]</div>"
                                }
                            },
                            {
                                "title": "2.2.3 Overall Risk-Benefit Assessment",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218525",
                                            "display": "ICH M11 Protocol Section 2.2.3 Overall Risk-Benefit Assessment"
                                        }
                                    ],
                                    "text": "section2.2.3-overall-risk-benefit-assessment"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide a succinct, concluding statement on the perceived balance between risks that have been identified from cumulative safety data, protocol procedures, and anticipated efficacy/benefits within the context of the proposed trial.]</div>"
                                }
                            }
                        ]
                    }
                ]
            }
        ]
    };
    let m11ReportSection03Json = generateM11Section3Composition(researchStudyJson, resourceDictionary);
    let m11ReportSection04Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-04"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218533",
                        "display": "ICH M11 Protocol Section 4 TRIAL DESIGN"
                    }
                ],
                "text": "section4-trial-design"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 4 (Trial Design) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "4 TRIAL DESIGN",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218533",
                            "display": "ICH M11 Protocol Section 4 TRIAL DESIGN"
                        }
                    ],
                    "text": "section4-trial-design"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "4.1 Description of Trial Design",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218534",
                                    "display": "ICH M11 Protocol Section 4.1 Description of Trial Design"
                                }
                            ],
                            "text": "section4.1-description-of-trial-design"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the overall trial design and intervention model (e.g., single group, parallel group, cross-over, factorial, sequential), the expected number of participants, and the control method (e.g., placebo, active comparator, low dose, external, standard of care, sham procedure, or none [uncontrolled]). If there are any key aspects of the investigational trial intervention that inform the selection of the intervention model, this should be described. If applicable, indicate other design characteristics (e.g., superiority, noninferiority, dose escalation, or equivalence). If the trial will have an adaptive or novel design (e.g., the trial will be conducted under a master protocol), provide a summary of these design aspects. If applicable, describe within-trial transition rules, e.g., transitions involving cohorts or trial parts. Dose escalation or dose-ranging details should also be described. Describe the trial duration with reference to Section 1.2 Trial Schema. Explain what the overall duration for an individual participant is anticipated to be and why, including the sequence and duration of trial periods (e.g., screening, run-in, randomisation, treatment [fixed dose/titration], follow-up/washout periods). Where applicable, include discussion of sentinel dosing (or lack thereof), dose escalation, and cohort expansion. If dose modification decisions are dependent upon review by a committee, include details in Section 11.4 Committees. State the method of assignment to trial intervention the level and method of blinding that will be used with reference to Section 6.7 Investigational Trial Intervention Assignment, Randomisation and Blinding. Describe any other important aspects of the design, e.g.: • geographic scope of trial (e.g., single-centre, multi-centre, or multi-centre and multi-national) • use of decentralised processes, tools, or features in the trial • planned use of a Data Monitoring Committee, or similar review group and cross reference Section 11.4 Committees, for details • whether an interim analysis is planned; if so, refer to details in Section 10.9 Interim Analyses • any planned extension trial, long-term follow-up/registry, planned future use of samples or data, or post-trial sample analysis or other data-related activities]</div>"
                        },
                        "section": [
                            {
                                "title": "4.1.1 Stakeholder Input into Design",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218535",
                                            "display": "ICH M11 Protocol Section 4.1.1 Stakeholder Input into Design"
                                        }
                                    ],
                                    "text": "section4.1.1-stakeholder-input-into-design"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any stakeholder (for example, patient, healthcare professional and patient advocacy groups) involvement in the design of the trial and any suggestions implemented.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "4.2 Rationale for Trial Design",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218536",
                                    "display": "ICH M11 Protocol Section 4.2 Rationale for Trial Design"
                                }
                            ],
                            "text": "section4.2-rationale-for-trial-design"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Enter Overall Rationale for Trial Design, if not using below optional subheadings.]</div>"
                        },
                        "section": [
                            {
                                "title": "4.2.1 Rationale for Estimand(s)",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218537",
                                            "display": "ICH M11 Protocol Section 4.2.1 Rationale for Estimand(s)"
                                        }
                                    ],
                                    "text": "section4.2.1-rationale-estimands"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[When estimands are associated with the Primary and Secondary Objectives described in Section 3 Trial Objectives and Associated Estimands, provide a rationale for the estimand(s) not described elsewhere in the document. This should include a rationale that the selected endpoint(s) are clinically relevant and provide a reliable and valid measurement of the intended intervention effect. It should also include a rationale for the selected strategies for handling intercurrent events.]</div>"
                                }
                            },
                            {
                                "title": "4.2.2 Rationale for Intervention Model",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218538",
                                            "display": "ICH M11 Protocol Section 4.2.2 Rationale for Intervention Model"
                                        }
                                    ],
                                    "text": "section4.2.2-rationale-intervention-model"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide a rationale for the trial intervention model described in Section 4.1 Description of Trial Design with a cross reference to Section 6.2 Rationale for Investigational Intervention Dose and Regimen. Rationale for choice of comparator, if applicable, should be described separately in Section 4.2.5 Rationale for Control Type. A rationale for the choice of trial population should be described separately in Section 5.1 Description of Trial Population and Rationale.]</div>"
                                }
                            },
                            {
                                "title": "4.2.3 Rationale for Control Type",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218539",
                                            "display": "ICH M11 Protocol Section 4.2.3 Rationale for Control Type"
                                        }
                                    ],
                                    "text": "section4.2.3-rationale-comparator"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, provide a rationale for the type and choice of control selected for the trial (e.g., placebo, active drug, combination, external). Describe any known or potential problems associated with the control group selected in light of the specific disease and intervention(s) being studied. If comparators will differ by region, describe. The rationale for dose/dose regimen is explained in Section 6.2 Rationale for Investigational Trial Intervention Dose and Regimen.]</div>"
                                }
                            },
                            {
                                "title": "4.2.4 Rationale for Trial Duration",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218540",
                                            "display": "ICH M11 Protocol Section 4.2.4 Rationale for Trial Duration"
                                        }
                                    ],
                                    "text": "section4.2.4-rationale-duration"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide a rationale that the trial duration is appropriate for a reliable and relevant evaluation of the trial intervention per the trial objective(s).]</div>"
                                }
                            },
                            {
                                "title": "4.2.5 Rationale for Adaptive or Novel Design",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218541",
                                            "display": "ICH M11 Protocol Section 4.2.5 Rationale for Adaptive or Novel Trial Design"
                                        }
                                    ],
                                    "text": "section4.2.5-rationale-adaptive"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, provide a rationale for the use of an adaptive or novel design.]</div>"
                                }
                            },
                            {
                                "title": "4.2.6 Rationale for Interim Analysis",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218542",
                                            "display": "ICH M11 Protocol Section 4.2.6 Rationale for Interim Analysis"
                                        }
                                    ],
                                    "text": "section4.2.6-rationale-interim-analysis"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, provide a rationale for any interim analysis planned with respect to its purpose (for example, stopping the trial early for efficacy or futility) and timing.]</div>"
                                }
                            },
                            {
                                "title": "4.2.7 Rationale for Other Trial Design Aspects",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218543",
                                            "display": "ICH M11 Protocol Section 4.2.7 Rationale for Other Trial Design Aspects"
                                        }
                                    ],
                                    "text": "section4.2.7-rationale-other-aspects"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Discuss rationale for any additional aspects of the design not addressed above.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "4.3 Trial Stopping Rules",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218544",
                                    "display": "ICH M11 Protocol Section 4.3 Trial Stopping Rules"
                                }
                            ],
                            "text": "section4.3-trial-stopping-rules"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any trial-specific stopping rules, including guidance on when the trial should be stopped for safety reasons, when a cohort or dose escalation should be terminated, and/or when a given treatment arm should be terminated. If applicable, describe any rules that may result in a temporary pause of dosing and/or enrollment into the trial and criteria for restarting enrollment. Ensure that the trials stopping rules are aligned with the specifications that are described in Section 10.9 for Interim Analyses.]</div>"
                        }
                    },
                    {
                        "title": "4.4 Start of Trial and End of Trial",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218545",
                                    "display": "ICH M11 Protocol Section 4.4 Start of Trial and End of Trial"
                                }
                            ],
                            "text": "section4.4-start-and-end"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Define key timepoints in the trial, including trial start and end definitions (e.g., a key timepoint definition for start of trial might be when the informed consent is signed by the first participant and a key timepoint definition for end of trial might be when participants are no longer being examined or the last participant’s last trial assessment has occurred). Consider local regulatory requirements for these and other definitions (e.g., the first act of recruitment). If appropriate, provide a cross reference to Section 11.11 Early Site Closure.]</div>"
                        }
                    },
                    {
                        "title": "4.5 Access to Trial Intervention After End of Trial",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218546",
                                    "display": "ICH M11 Protocol Section 4.5 Access to Trial Intervention After End of Trial"
                                }
                            ],
                            "text": "section4.5-access-after-trial"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any possibilities for access to trial intervention, if any, beyond completion of the trial. Planned extension trials, if described in Section 4.1 Description of Trial Design, do not need to be repeated in this section.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection05Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-05"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218547",
                        "display": "ICH M11 Protocol Section 5 TRIAL POPULATION"
                    }
                ],
                "text": "section5-trial-population"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 5 (Trial Population) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "5 TRIAL POPULATION",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218547",
                            "display": "ICH M11 Protocol Section 5 TRIAL POPULATION"
                        }
                    ],
                    "text": "section5-trial-population"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": populationSectionSection
            }
        ]
    };
    let m11ReportSection06Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-06"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218560",
                        "display": "ICH M11 Protocol Section 6 TRIAL INTERVENTION AND CONCOMITANT THERAPY"
                    }
                ],
                "text": "section6-trial-intervention"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 6 (Trial Intervention) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "6 TRIAL INTERVENTION AND CONCOMITANT THERAPY",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218560",
                            "display": "ICH M11 Protocol Section 6 TRIAL INTERVENTION AND CONCOMITANT THERAPY"
                        }
                    ],
                    "text": "section6-trial-intervention"
                },
                "text": {
                    "status": "empty",
                    "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[Trial interventions are all pre-specified investigational and noninvestigational medicinal products, medical devices or other interventions intended for the participants during the trial. The investigational trial intervention is the product used in the trial as part of trial objectives. Description of the investigational trial intervention is provided in Section 6.1 Description of Trial Intervention. Other trial interventions that are not part of trial objectives or do not have an investigational role in this trial are described in Section 6.9 Description of Noninvestigational Trial Interventions. Any regional requirements should be noted in the appropriate subsections. Provide an overview of investigational and noninvestigational trial interventions. Designate the trial intervention as IMP or NIMP/AxMP based on trial design and regional requirements. Consider an optional table with 12 columns (Arm Name, Arm Type, Intervention Name, Intervention Type, Pharmaceutical Dose Form, Dosage Strength(s), Dosage Level(s), Route of Administration, Regimen/Treatment Period/Vaccination Regimen, Use, IMP/NIMP, Sourcing).]<table><tbody><tr><td>Arm Name</td><td>Arm Type</td><td>Intervention Name</td><td>Intervention Type</td><td>Pharmaceutical Dose Form</td><td>Dosage Strength(s)</td><td>Dosage Level(s)</td><td>Route of Administration</td><td>Regimen/Treatment Period/Vaccination Regimen</td><td>Use</td><td>IMP/NIMP</td><td>Sourcing</td></tr><tr><td>[Enter data]</td><td>[Enter one of: Active Comparator Arm (C174267), Control Arm (C174226), Experimental Arm (C174266), No Intervention Arm (C174270), Placebo Comparator Arm (C174268), Sham Comparator Arm (C174269)]</td><td>[Enter data]</td><td>[Enter one of: Drug (C1909), Medical Device (C16830), Biologic (C307), Vaccine (C923), Non-Surgical Procedure (C218507), Surgery (C15329), Radiation (C15313), Behavioral (C15184), Genetic (C15238), Dietary Supplement (C1505), Combination Product (C54696), Diagnostic (C18020)]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter one of: Experimental Intervention (C41161), Placebo (C753), Rescue Medicine (C165835), Background Treatment (C165822), Challenge Agent (C158128), Diagnostic (C18020), Additional Required Treatment (C207614)]</td><td>[Enter IMP (C202579) or NIMP (C156473)]</td><td>[Enter Centrally Sourced (C215659) and/or Locally Sourced (C215660) ]</td></tr><tr><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td></tr></tbody></table><p>IMP=Investigational Medicinal Product; NIMP=NonInvestigational/Auxiliary Medicinal Product.</p></div>'
                },
                "section": [
                    {
                        "title": "6.1 Description of Investigational Trial Intervention",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218561",
                                    "display": "ICH M11 Protocol Section 6.1 Description of Investigational Trial Intervention"
                                }
                            ],
                            "text": "section6.1-description-of-trial-intervention"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the investigational trial intervention to be administered in each arm of the trial and for each period of the trial including route and mode of administration, dose, dosage regimen, duration of intervention, use, packaging and labelling. Refer to approved regional labelling, as appropriate. For investigational drug/device combination products, include details on the configuration and use of the device and device manufacturer. A device user manual may be referenced in this section. Entries should Reference ExposureDefinition Profile of Group Resource.]</div>"
                        }
                    },
                    {
                        "title": "6.2 Rationale for Investigational Trial Intervention Dose and Regimen",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218562",
                                    "display": "ICH M11 Protocol Section 6.2 Rationale for Investigational Trial Intervention Dose and Regimen"
                                }
                            ],
                            "text": "section6.2-rationale-for-trial-intervention-regimen"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide a rationale for the selection of the dose(s) or dose range, pharmaceutical dose form, route of administration, and dosing regimen of the investigational trial intervention, as applicable. This rationale should include relevant results from nonclinical studies and clinical trials that support selection of the dose and regimen. Discuss impact of differences in trial population characteristics (e.g., age, sex, race) which could lead to differences in pharmacokinetics and pharmacodynamics in this trial as compared to previous trials. If applicable, justify any differences in dose regimen or therapeutic use relative to approved labelling. Describe prior trials and other information that support the dose and/or dose regimen of the investigational trial intervention. Include a rationale for prospective dose adjustments incorporated in the trial, if any. Entries should Reference ExposureDefinition Profile of Group Resource.]</div>"
                        }
                    },
                    {
                        "title": "6.3 Investigational Trial Intervention Administration",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218563",
                                    "display": "ICH M11 Protocol Section 6.3 Investigational Trial Intervention Administration"
                                }
                            ],
                            "text": "section6.3-dosing-and-administration"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the detailed procedures for administration of each participant’s dose of each investigational trial intervention. This may include the timing of dosing (e.g., time of day, interval), the duration (e.g., the length of time participants will be administered the investigational trial intervention), and the timing of dosing relative to meals. Include any specific instructions on who, when or how to prepare and take the dose(s) and how to handle any delayed or missed doses. Dose escalation or cohort expansion as part of the overall design should be covered in Section 4.1 Description of Trial Design. Entries should Reference ActivityDefinition Resource.]</div>"
                        }
                    },
                    {
                        "title": "6.4 Investigational Trial Intervention Dose Modification",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218564",
                                    "display": "ICH M11 Protocol Section 6.4 Investigational Trial Intervention Dose Modification"
                                }
                            ],
                            "text": "section6.4-dose-modification"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[For each participant, describe any dose modifications allowed, including conditions for such dose modifications, particularly regarding failure to respond or safety concerns. State any minimum period required before a participant’s dose might be raised to the next higher dose or dose range. Include whether it is permissible to start and stop treatment and how dose reductions (if permitted) are to be managed. Information on stopping investigational trial intervention for participants due to safety/other reasons should be described in Section 7 Participant Discontinuation of Trial Intervention and Discontinuation or Withdrawal from Trial. Entries should Reference ActivityDefinition Resource.]</div>"
                        }
                    },
                    {
                        "title": "6.5 Management of Investigational Trial Intervention Overdose",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218565",
                                    "display": "ICH M11 Protocol Section 6.5 Management of Investigational Trial Intervention Overdose"
                                }
                            ],
                            "text": "section6.5-management-of-overdose"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe what is meant by investigational trial intervention overdose. Provide any available information on managing the overdose and ensure it is consistent with the Investigator’s Brochure or product labelling. Cross reference these documents as applicable. Entries should Reference ActivityDefinition Resource or PlanDefinition Resource.]</div>"
                        }
                    },
                    {
                        "title": "6.6 Preparation, Storage, Handling and Accountability of Investigational Trial Intervention",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218566",
                                    "display": "ICH M11 Protocol Section 6.6 Preparation, Storage, Handling and Accountability of Investigational Trial Intervention"
                                }
                            ],
                            "text": "section6.6-preparation-storage-handling"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "6.6.1 Preparation of Investigational Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218567",
                                            "display": "ICH M11 Protocol Section 6.6.1 Preparation of Investigational Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.6.1-preparation"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any preparation of the investigational trial intervention, and when necessary, who should prepare it. When applicable, describe the maximum hold time once thawed/mixed before administration. Include thawing, diluting, mixing, and reconstitution/preparation instructions. For drug/device combination products, include any relevant assembly or use instructions and reference the package insert that is provided separately. If the instructions are lengthy or complicated, it is acceptable to reference the package insert (if applicable) or include instructions in separate documents provided to the site (e.g., a pharmacy manual and reference the separate documents.]</div>"
                                }
                            },
                            {
                                "title": "6.6.2 Storage and Handling of Investigational Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218568",
                                            "display": "ICH M11 Protocol Section 6.6.2 Storage and Handling of Investigational Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.6.2-storage-handling"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe storage and handling requirements (e.g., protection from light, temperature, humidity) for the investigational trial intervention(s). For trials in which multi-dose vials are utilised, provide additional information regarding stability and expiration time after initial use (e.g., if the seal is broken). Explain how the investigational trial intervention will be provided to the Investigator. If applicable, include details about kits, packaging, or other material of the investigational trial intervention for blinding purposes. If the instructions are lengthy or complicated, it is acceptable to reference the package insert (if applicable) or include instructions in separate documents provided to the site (e.g., a pharmacy manual) and reference the separate documents.]</div>"
                                }
                            },
                            {
                                "title": "6.6.3 Accountability of Investigational Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218569",
                                            "display": "ICH M11 Protocol Section 6.6.3 Accountability of Investigational Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.6.3-accountability"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the accountability method, including: • how the investigational trial intervention will be distributed • who will distribute the investigational trial intervention • participation of a drug storage repository or pharmacy, if applicable • plans for disposal or return of unused product • if applicable, plans for reconciliation of investigational trial intervention]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "6.7 Investigational Trial Intervention Assignment, Randomisation and Blinding",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218570",
                                    "display": "ICH M11 Protocol Section 6.7 Investigational Trial Intervention Assignment, Randomisation and Blinding"
                                }
                            ],
                            "text": "section6.7-assignment-randomisation-blinding"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "6.7.1 Participant Assignment to Investigational Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218571",
                                            "display": "ICH M11 Protocol Section 6.7.1 Participant Assignment to Investigational Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.7.1-assignment"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[State that at enrollment, participant identification codes should be assigned. Describe the method of assigning participants to investigational trial intervention without being so specific that blinding or randomisation might be compromised. If assignment to investigational trial intervention is by randomisation, describe when randomisation occurs relative to screening. If adaptive randomisation or other methods of covariate balancing/minimisation are employed, include a cross reference to the methods of analysis in Section 10 Statistical Considerations. As applicable, details regarding the implementation of procedures to minimise bias should be described.]</div>"
                                }
                            },
                            {
                                "title": "6.7.2 Randomisation",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218572",
                                            "display": "ICH M11 Protocol Section 6.7.2 Randomisation"
                                        }
                                    ],
                                    "text": "section6.7.2-randomisation"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the randomisation procedures (e.g., central randomisation procedures), the method used to generate the randomisation schedule (e.g., computer generated), the source of the randomisation schedule (e.g., sponsor, investigator, or other), and whether IxRS will be used. To maintain the integrity of the blinding, do not include the block size.]</div>"
                                }
                            },
                            {
                                "title": "6.7.3 Measures to Maintain Blinding",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218573",
                                            "display": "ICH M11 Protocol Section 6.7.3 Measures to Maintain Blinding"
                                        }
                                    ],
                                    "text": "section6.7.3-blinding"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe measures that will be used to maintain blinding: • The investigational trial interventions are as indistinguishable as possible • Plans for the maintenance of randomisation codes and appropriate blinding for the trial • Procedures for planned (e.g., interim analysis) and unintentional (e.g., breach of procedure) breaking of randomisation codes. For unplanned but intentional actions (e.g., safety events), refer to Section 6.7.4 Emergency Unblinding at the Site. If the trial allows for some investigators or other designated staff to remain unblinded (e.g., to allow them to adjust investigational trial intervention), the means of maintaining the blinding for other investigators or staff should be explained. Measures to prevent unblinding by laboratory measurements or while performing study assessments, if used, should be described.]</div>"
                                }
                            },
                            {
                                "title": "6.7.4 Emergency Unblinding at the Site",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218574",
                                            "display": "ICH M11 Protocol Section 6.7.4 Emergency Unblinding at the Site"
                                        }
                                    ],
                                    "text": "section6.7.4-unblinding"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the criteria for breaking the trial blind or participant code. Describe the circumstances that would require breaking the blind, either for an individual participant or all participants, and specify who will be responsible for this decision. Include the procedure for emergency unblinding as well as documentation of unblinding. Indicate to whom the intentional and unplanned unblinding should be reported.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "6.8 Investigational Trial Intervention Adherence",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218575",
                                    "display": "ICH M11 Protocol Section 6.8 Investigational Trial Intervention Adherence"
                                }
                            ],
                            "text": "section6.8-intervention-adherence"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the measures to monitor and document participants’ adherence with investigational trial intervention (e.g., trial intervention accountability records, diary cards, or investigational trial intervention concentration measurements). List what documents are mandatory to complete (e.g., participant drug log) and what source data/records will be used to document investigational trial intervention adherence.]</div>"
                        }
                    },
                    {
                        "title": "6.9 Description of Noninvestigational Trial Intervention",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218576",
                                    "display": "ICH M11 Protocol Section 6.9 Description of Noninvestigational Trial Intervention"
                                }
                            ],
                            "text": "section6.9-noninvestigational-interventions"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[As stated in Section 6 Trial Intervention and Concomitant Therapy, noninvestigational interventions are pre-specified products used in the trial but are not part of trial objectives and hence, are not investigational trial interventions.]</div>"
                        },
                        "section": [
                            {
                                "title": "6.9.1 Background Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218577",
                                            "display": "ICH M11 Protocol Section 6.9.1 Background Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.9.1-background-intervention"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any background intervention(s), including administration and any conditions for use.]</div>"
                                }
                            },
                            {
                                "title": "6.9.2 Rescue Therapy",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218578",
                                            "display": "ICH M11 Protocol Section 6.9.2 Rescue Therapy"
                                        }
                                    ],
                                    "text": "section6.9.2-rescue-therapy"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[List all permitted rescue medications, treatments, and/or procedures, including any relevant instructions on administration and any conditions of use. If administration of rescue therapy leads to the temporary discontinuation of trial intervention or a participant’s withdrawal from the trial, refer to Section 7 Participant Discontinuation of Trial Intervention and Discontinuation or Withdrawal from Trial.]</div>"
                                }
                            },
                            {
                                "title": "6.9.3 Other Noninvestigational Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218579",
                                            "display": "ICH M11 Protocol Section 6.9.3 Other Noninvestigational Trial Intervention"
                                        }
                                    ],
                                    "text": "section6.9.3-other-therapy"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe the use of any other noninvestigational trial intervention, e.g., challenge agents or diagnostics.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "6.10 Concomitant Therapy",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218580",
                                    "display": "ICH M11 Protocol Section 6.10 Concomitant Therapy"
                                }
                            ],
                            "text": "section6.10-concomitant-therapy"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the concomitant medications, supplements, complementary and alternative therapies, treatments, and/or procedures which are prohibited or permitted during the trial and include details about when the information will be collected (e.g., during screening, at each visit). When appropriate to separate the content, subheadings may be used.]</div>"
                        },
                        "section": [
                            {
                                "title": "6.10.1 Prohibited Concomitant Therapy",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218581",
                                            "display": "ICH M11 Protocol Section 6.10.1 Prohibited Concomitant Therapy"
                                        }
                                    ],
                                    "text": "section6.10.1-prohibited-concomitant-therapy"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any prohibited concomitant therapy.]</div>"
                                }
                            },
                            {
                                "title": "6.10.2 Permitted Concomitant Therapy",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218582",
                                            "display": "ICH M11 Protocol Section 6.10.2 Permitted Concomitant Therapy"
                                        }
                                    ],
                                    "text": "section6.10.2-permitted-concomitant-therapy"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If applicable, describe any permitted concomitant therapy.]</div>"
                                }
                            }
                        ]
                    }
                ]
            }
        ]
    };
    let m11ReportSection07Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-07"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218583",
                        "display": "ICH M11 Protocol Section 7 PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL"
                    }
                ],
                "text": "section7-participant-discontinuation"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 7 (Participant Discontinuation) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "7 PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218583",
                            "display": "ICH M11 Protocol Section 7 PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL"
                        }
                    ],
                    "text": "section7-participant-discontinuation"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "7.1 Discontinuation of Trial Intervention for Individual Participants",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218584",
                                    "display": "ICH M11 Protocol Section 7.1 Discontinuation of Trial Intervention for Individual Participants"
                                }
                            ],
                            "text": "section7.1-discontinuation-of-trial-intervention"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "7.1.1 Permanent Discontinuation of Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218585",
                                            "display": "ICH M11 Protocol Section 7.1.1 Permanent Discontinuation of Trial Intervention"
                                        }
                                    ],
                                    "text": "section7.1.1-permanent-discontinuation-of-trial-intervention"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe: • the criteria for discontinuation of a participant from any trial intervention, carefully evaluating which are appropriate for the trial population and therapy being studied • how participants who discontinue trial intervention will be followed after discontinuation. Depending on the chosen intercurrent event handling strategy, it will be important to continue to follow and ascertain outcomes in participants who discontinue treatment through the end of the trial to prevent missing data in important analyses. Refer to Section 1.3 Schedule of Activities for assessments to be performed at the time of and following discontinuation of trial intervention • the process for collecting and recording the detailed reasons for discontinuing trial intervention if not described elsewhere]</div>"
                                }
                            },
                            {
                                "title": "7.1.2 Temporary Discontinuation of Trial Intervention",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218586",
                                            "display": "ICH M11 Protocol Section 7.1.2 Temporary Discontinuation of Trial Intervention"
                                        }
                                    ],
                                    "text": "section7.1.2-temporary-discontinuation-of-trial-intervention"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe: • the criteria for temporary discontinuation or interruption of trial intervention for an individual participant • what to do and which restrictions still apply if the participant has to temporarily discontinue or interrupt trial intervention • whether the participant will continue in the trial • which assessments will be performed for the stated duration of the trial. Details of any rechallenge or restart after a safety-related event should be included in Section 7.1.3 Rechallenge.]</div>"
                                }
                            },
                            {
                                "title": "7.1.3 Rechallenge",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218587",
                                            "display": "ICH M11 Protocol Section 7.1.3 Rechallenge"
                                        }
                                    ],
                                    "text": "section7.1.3-rechallenge"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the criteria for rechallenge/restarting trial intervention, how and when to perform rechallenge, number of rechallenges allowed during the trial, and whether all, or specify which, assessments will be performed for the stated duration of the trial. If rechallenge is not allowed, state this.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "7.2 Participant Discontinuation or Withdrawal from the Trial",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218588",
                                    "display": "ICH M11 Protocol Section 7.2 Participant Discontinuation or Withdrawal from the Trial"
                                }
                            ],
                            "text": "section7.2-participant-withdrawal"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the criteria for participant discontinuation or withdrawal from the trial. Describe the reason for withdrawal and the type of data to be collected for the final assessments with reference to the schedule of activities for the participant’s end of study visit unless provided in another section. In many cases, the only reason for a participant being considered withdrawn from the trial should be a participant’s withdrawal of consent to continue to participate in the trial. All other participants, including those who discontinue treatment, should remain in the trial and continue to be followed to prevent missing data in important analyses. Refer to Section 10 Statistical Considerations for the data that must be collected for the trial estimands.]</div>"
                        }
                    },
                    {
                        "title": "7.3 Management of Loss to Follow-Up",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218589",
                                    "display": "ICH M11 Protocol Section 7.3 Management of Loss to Follow-Up"
                                }
                            ],
                            "text": "section7.3-lost-to-follow-up"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe how the trial will define how participants are lost to follow-up. In general, participants should be considered lost to follow-up only if they cannot be reached despite multiple attempts to contact. Also describe approaches that will be used to minimise loss to follow-up, such as multiple, diverse methods to remain in contact with participants (e.g., telephone calls, texts, and emails to the participant) and how contacts will be recorded.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection08Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-08"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218590",
                        "display": "ICH M11 Protocol Section 8 TRIAL ASSESSMENTS AND PROCEDURES"
                    }
                ],
                "text": "section8-assessments"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 8 (Trial Assessments) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "8 TRIAL ASSESSMENTS AND PROCEDURES",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218590",
                            "display": "ICH M11 Protocol Section 8 TRIAL ASSESSMENTS AND PROCEDURES"
                        }
                    ],
                    "text": "section8-assessments"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "8.1 Trial Assessments and Procedures Considerations",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218591",
                                    "display": "ICH M11 Protocol Section 8.1 Trial Assessments and Procedures Considerations"
                                }
                            ],
                            "text": "section8.1-assessments-procedures"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe general considerations applicable across trial assessments and procedures.]</div>"
                        }
                    },
                    {
                        "title": "8.2 Screening/Baseline Assessments and Procedures",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218592",
                                    "display": "ICH M11 Protocol Section 8.2 Screening/Baseline Assessments and Procedures"
                                }
                            ],
                            "text": "section8.2-screening-baseline-assessments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any assessments and procedures that are unique to screening/baseline (e.g., collection of data on participant characteristics, assessments/procedures performed for the purpose of determining eligibility or for stratification) in this section. Describe screening and baseline assessments and procedures separately when screening and baseline are different or performed at different visits.]</div>"
                        }
                    },
                    {
                        "title": "8.3 Efficacy Assessments and Procedures",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218593",
                                    "display": "ICH M11 Protocol Section 8.3 Efficacy Assessments and Procedures"
                                }
                            ],
                            "text": "section8.3-efficacy-assessments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe efficacy assessments and procedures in this section. Cross reference Section 8.7 Immunogenicity Assessments if immunogenicity assessments are used in efficacy determination.]</div>"
                        }
                    },
                    {
                        "title": "8.4 Safety Assessments and Procedures",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218594",
                                    "display": "ICH M11 Protocol Section 8.4 Safety Assessments and Procedures"
                                }
                            ],
                            "text": "section8.4-safety-assessments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe safety assessments and procedures utilizing the following subsections as applicable. Add level 3 headings as needed. • Identify any noninvestigator party responsible for evaluation of laboratory or other safety assessments (e.g., Sponsor or external Independent Data Monitoring Committee; cross refer to Section 11.4 Committees for details as applicable). • Include guidelines for the medical management of relevant laboratory or other safety assessment abnormalities.]</div>"
                        },
                        "section": [
                            {
                                "title": "8.4.1 Physical Examination",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218595",
                                            "display": "ICH M11 Protocol Section 8.4.1 Physical Examination"
                                        }
                                    ],
                                    "text": "section8.4.1-physical-examination"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection and interpretation of physical examinations.]</div>"
                                }
                            },
                            {
                                "title": "8.4.2 Vital Signs",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218596",
                                            "display": "ICH M11 Protocol Section 8.4.2 Vital Signs"
                                        }
                                    ],
                                    "text": "section8.4.2-vital-signs"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection and interpretation of vital signs.]</div>"
                                }
                            },
                            {
                                "title": "8.4.3 Electrocardiograms",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218597",
                                            "display": "ICH M11 Protocol Section 8.4.3 Electrocardiograms"
                                        }
                                    ],
                                    "text": "section8.4.3-electrocardiograms"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection, interpretation, and archiving of ECGs.]</div>"
                                }
                            },
                            {
                                "title": "8.4.4 Clinical Laboratory Assessments",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218598",
                                            "display": "ICH M11 Protocol Section 8.4.4 Clinical Laboratory Assessments"
                                        }
                                    ],
                                    "text": "section8.4.4-clinical-laboratory-assessments"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any specific instructions for the collection and interpretation of clinical laboratory assessments, including: • type of laboratory (central/local/hybrid) • acceptability of additional tests deemed necessary by the investigator or local regulations • instructions for situations in which central laboratory results are not available in time for trial intervention and/or response evaluation, or in the event of a severe disruption (e.g., a pandemic or natural disaster) • treatment algorithms for results out of normal range • cross reference Section 12.1 Clinical Laboratory Tests for laboratory assessment panels]</div>"
                                }
                            },
                            {
                                "title": "8.4.5 Pregnancy Testing",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218599",
                                            "display": "ICH M11 Protocol Section 8.4.5 Pregnancy Testing"
                                        }
                                    ],
                                    "text": "section8.4.5-pregnancy-testing"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection and interpretation of pregnancy testing.]</div>"
                                }
                            },
                            {
                                "title": "8.4.6 Suicidal Ideation and Behaviour Risk Monitoring",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218600",
                                            "display": "ICH M11 Protocol Section 8.4.6 Suicidal Ideation and Behaviour Risk Monitoring"
                                        }
                                    ],
                                    "text": "section8.4.6-suicidal-ideation"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If the trial meets any of the criteria requiring suicidal ideation and behaviour risk monitoring by the guidance/guideline in each region, include justification for the need for suicidal ideation and behaviour risk monitoring in the study and add any specific instructions for the collection and interpretation of the assessment. In case this is an AESI in the study, justification should also be provided in Section 9.2.4 Adverse Events of Special Interest.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "8.5 Pharmacokinetics",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218601",
                                    "display": "ICH M11 Protocol Section 8.5 Pharmacokinetics"
                                }
                            ],
                            "text": "section8.5-pharmacokinetics"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection and assay of samples and interpretation of PK assessments. • Describe the biological samples collected, the handling of samples, and the assay method. o Specific sample collection and processing instructions can be described in an appendix or a separate document and cross referenced. • Describe the retention time for the samples (ensuring alignment with the ICF). • Indicate the types of analyses for each sample. • Define the PK parameters to be calculated and the calculation methods.]</div>"
                        }
                    },
                    {
                        "title": "8.6 Biomarkers",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218602",
                                    "display": "ICH M11 Protocol Section 8.6 Biomarkers"
                                }
                            ],
                            "text": "section8.6-biomarkers"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only). Include any specific instructions for the collection of samples and assessment of biomarkers, including pharmacodynamics. If biomarker or pharmacodynamic testing is not included in the study, state “Not Applicable.” •	Describe the biological samples that will be collected (for example, tissue, serum, plasma, etc.). o	Specific sample collection and processing instructions can be described in an appendix or a separate document and cross-referenced. •	Describe the retention time for the samples (ensuring alignment with the ICF). •	Indicate the types of biomarkers that will be studied for each sample. •	Specify whether each sample is optional or required. Required samples must be based on a protocol objective.]</div>"
                        },
                        "section": [
                            {
                                "title": "8.6.1 Genetics, Genomics, Pharmacogenetics, and Pharmacogenomics",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218603",
                                            "display": "ICH M11 Protocol Section 8.6.1 Genetics, Genomics, Pharmacogenetics, and Pharmacogenomics"
                                        }
                                    ],
                                    "text": "section8.6.1-genetics-pharmacogenomics"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection and assay of samples for genetic, genomic, pharmacogenetic and/or pharmacogenomic analysis. • Describe the biological samples that will be collected (for example, tissue, serum, plasma, etc.), handling of samples, and the assay method. o Specific sample collection and processing instructions can be described in an appendix or a separate document and cross-referenced. • Describe the retention time for the samples (ensuring alignment with the ICF). • Indicate the types of analyses that may be studied for each sample.]</div>"
                                }
                            },
                            {
                                "title": "8.6.2 Pharmacodynamic Biomarkers",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218604",
                                            "display": "ICH M11 Protocol Section 8.6.2 Pharmacodynamic Biomarkers"
                                        }
                                    ],
                                    "text": "section8.6.2-pharmacodynamics"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection of samples and assessment of pharmacodynamic biomarkers. • Describe the biological samples that will be collected (for example, tissue, serum, plasma, etc.). o Specific sample collection and processing instructions can be described in an appendix or a separate document and cross-referenced. • Describe the retention time for the samples (ensuring alignment with the ICF). • Indicate the types of biomarkers that will be studied for each sample. • Specify whether each sample is optional or required. Required samples must be based on a protocol objective.]</div>"
                                }
                            },
                            {
                                "title": "8.6.3 Other Biomarkers",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218605",
                                            "display": "ICH M11 Protocol Section 8.6.3 Other Biomarkers"
                                        }
                                    ],
                                    "text": "section8.6.3-other"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection of samples and assessment of other biomarkers. • Describe the biological samples that will be collected (for example, tissue, serum, plasma, etc.). o Specific sample collection and processing instructions can be described in an appendix or a separate document and cross-referenced. • Describe the retention time for the samples (ensuring alignment with the ICF). • Indicate the types of biomarkers that will be studied for each sample. • Specify whether each sample is optional or required. Required samples must be based on a protocol objective.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "8.7 Immunogenicity Assessments",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218606",
                                    "display": "ICH M11 Protocol Section 8.7 Immunogenicity Assessments"
                                }
                            ],
                            "text": "section8.7-immunogenicity-assessments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Include any specific instructions for the collection of samples and interpretation of immunogenicity. If immunogenicity assessments are included within Efficacy Assessments or Safety Assessments, cross-reference to that section. •	Describe the biological samples that will be collected (e.g., tissue, serum, plasma). o Specific sample collection and processing instructions can be described in an appendix or a separate document and cross referenced. • Describe the retention time for the samples (ensuring alignment with the ICF). • Indicate the types of biomarkers that will be studied for each sample. • Specify whether each sample is optional or required. Required samples must be based on a protocol objective.]</div>"
                        }
                    },
                    {
                        "title": "8.8 Medical Resource Utilisation and Health Economics",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218607",
                                    "display": "ICH M11 Protocol Section 8.8 Medical Resource Utilisation and Health Economics"
                                }
                            ],
                            "text": "section8.8-economics"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[This section does not apply to COAs. Include this section only for any value evidence and outcomes assessments not included in either the efficacy or safety sections. Describe the health outcome measures, collection method (e.g., diary, physician interview), and participant burden.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection09Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-09"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218608",
                        "display": "ICH M11 Protocol Section 9 ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS"
                    }
                ],
                "text": "section9-adverse-events"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 9 (Adverse Events) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "9 ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218608",
                            "display": "ICH M11 Protocol Section 9 ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS"
                        }
                    ],
                    "text": "section9-adverse-events"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "9.1 Definitions",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218609",
                                    "display": "ICH M11 Protocol Section 9.1 Definitions"
                                }
                            ],
                            "text": "section9.1-definitions"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "9.1.1 Definitions of Adverse Events",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218610",
                                            "display": "ICH M11 Protocol Section 9.1.1 Definitions of Adverse Events"
                                        }
                                    ],
                                    "text": "section9.1.1-definitions-adverse-events"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the AE definitions, including: • any relevant regional AE requirements • any events that meet and do not meet the AE definition • any trial-specific AE clarifications • if applicable, any clarifications on the AE and SAE definitions for efficacy trials (e.g., lack of efficacy or failure of pharmacological actions reporting)]</div>"
                                }
                            },
                            {
                                "title": "9.1.2 Definitions of Serious Adverse Events",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218611",
                                            "display": "ICH M11 Protocol Section 9.1.2 Definitions of Serious Adverse Events"
                                        }
                                    ],
                                    "text": "section9.1.2-definitions-serious-adverse-events"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the SAE definitions, including: • Any relevant regional SAE requirements. •	Any events that meet and do not meet the SAE definition. • Any trial-specific SAE clarifications.]</div>"
                                }
                            },
                            {
                                "title": "9.1.3 Definitions of Product Complaints",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218612",
                                            "display": "ICH M11 Protocol Section 9.1.3 Definitions of Product Complaints"
                                        }
                                    ],
                                    "text": "section9.1.3-definitions-product-complaints"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the definitions of product complaints in the context of the trial. If appropriate, include 9.1.3.1 Definition of Medical Device Product Complaints.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "9.2 Timing and Procedures for Collection and Reporting",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218614",
                                    "display": "ICH M11 Protocol Section 9.2 Timing and Procedures for Collection and Reporting"
                                }
                            ],
                            "text": "section9.2-collection-methods"
                        },
                        "text": {
                            "status": "empty",
                            "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[Specify timing and procedures for collection and reporting of AEs, SAEs, product complaints (including medical device product complaints if applicable) and pregnancy and postpartum information in the sections below. This information may be summarized in a tabular format with 7 columns: Event Type, Situational Scope, Reportable Period Start, Reportable Period End, Timing for Reporting to Sponsor or Designee, Method for Reporting, Back-up Method for Reporting.]</p><table><tbody><tr><td>Event Type</td><td>Situational Scope</td><td>Reportable Period Start</td><td>Reportable Period End</td><td>Timing for Reporting to Sponsor or Designee</td><td>Method for Reporting</td><td>Back-up Method for Reporting</td></tr><tr><td>[Enter one of: Adverse Event (C41331), Serious Adverse Event (C41335), Trial Intervention Complaint (C218508), Drug/Device Combination Product Complaint (C222331), Pregnancy Event (C25742), Lactation Event (C218510), Post-Partum Event (C218511), Reportable Adverse Event of Special Interest (C218512), Not Reportable Adverse Event of Special Interest (C218513), Other (C17649)]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td></tr><tr><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td><td>[Enter data]</td></tr></tbody></table></div>'
                        },
                        "section": [
                            {
                                "title": "9.2.1 Timing",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218615",
                                            "display": "ICH M11 Protocol Section 9.2.1 Timing"
                                        }
                                    ],
                                    "text": "section9.2.1-timing"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify timing for collection and reporting, including: • start and end dates for collection and reporting • frequency of collection and reporting • cross reference to the Schedule of Assessments as appropriate]</div>"
                                }
                            },
                            {
                                "title": "9.2.2 Collection Procedures",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218616",
                                            "display": "ICH M11 Protocol Section 9.2.2 Collection Procedures"
                                        }
                                    ],
                                    "text": "section9.2.2-collection-procedures"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify procedures for collection and recording of AEs, SAEs, product complaints (including medical device product complaints if applicable) and pregnancy and postpartum information in the sections below.]</div>"
                                },
                                "section": [
                                    {
                                        "title": "Identification",
                                        "code": {
                                            "text": "section9.2.2.1-identification"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify how information will be identified (e.g., spontaneous reporting, solicited questions).]</div>"
                                        }
                                    },
                                    {
                                        "title": "Severity",
                                        "code": {
                                            "text": "section9.2.2.2-severity"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the intensity rating categories/scale.]</div>"
                                        }
                                    },
                                    {
                                        "title": "Causality",
                                        "code": {
                                            "text": "section9.2.2.3-causality"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the causality categories/scale and the procedures for assessing causality.]</div>"
                                        }
                                    },
                                    {
                                        "title": "Recording",
                                        "code": {
                                            "text": "section9.2.2.4-recording"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify procedures for recording.]</div>"
                                        }
                                    },
                                    {
                                        "title": "Follow-up",
                                        "code": {
                                            "text": "section9.2.2.5-followup"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the procedures for follow-up. Include the assessment tools that will be used to monitor the events and the duration of follow-up after appearance of the events.]</div>"
                                        }
                                    }
                                ]
                            },
                            {
                                "title": "9.2.3 Reporting",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218617",
                                            "display": "ICH M11 Protocol Section 9.2.3 Reporting"
                                        }
                                    ],
                                    "text": "section9.2.3-reporting-events"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the reporting method (e.g., an electronic data collection tool or a paper CRF), backup reporting method if applicable and reporting timeline to the Sponsor]</div>"
                                },
                                "section": [
                                    {
                                        "title": "9.2.3.1 Regulatory Reporting Requirements",
                                        "code": {
                                            "coding": [
                                                {
                                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                    "code": "C218618",
                                                    "display": "ICH M11 Protocol Section 9.2.3.1 Regulatory Reporting Requirements"
                                                }
                                            ],
                                            "text": "section9.2.3.1-regulatory-requirements"
                                        },
                                        "text": {
                                            "status": "empty",
                                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify: • the Investigator’s responsibilities for reporting to the Sponsor (and to Ethics Committees, where required), specifying timing of reporting to allow the Sponsor to meet their responsibilities • the Sponsor’s legal/regulatory responsibilities to report to regulatory authorities, ethics committees, and investigators • serious and unexpected adverse reaction reporting]</div>"
                                        }
                                    }
                                ]
                            },
                            {
                                "title": "9.2.4 Adverse Events of Special Interest",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218619",
                                            "display": "ICH M11 Protocol Section 9.2.4 Adverse Events of Special Interest"
                                        }
                                    ],
                                    "text": "section9.2.4-special-interest"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify any AESI: • any event (serious or nonserious) of scientific and medical concern relative to the trial intervention, for which ongoing monitoring and rapid communication by the investigator to the sponsor can be appropriate • other events that merit reporting to the Sponsor, trial leadership, IRB, and regulatory agencies. Include the following for each AESI: • the definition • the approach for ascertaining information • if applicable, any approach to confirm or adjudicate the occurrence]</div>"
                                }
                            },
                            {
                                "title": "9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218620",
                                            "display": "ICH M11 Protocol Section 9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs"
                                        }
                                    ],
                                    "text": "section9.2.5-disease-related-events"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify any DREs, DROs, or both that will not be reported as AEs or SAEs (e.g., seizures in anticonvulsant trials) or state “Not applicable.”]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "9.3 Pregnancy and Postpartum Information",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218621",
                                    "display": "ICH M11 Protocol Section 9.3 Pregnancy and Postpartum Information"
                                }
                            ],
                            "text": "section9.3-pregnancy-postpartum"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "9.3.1 Participants Who Become Pregnant During the Trial",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218622",
                                            "display": "ICH M11 Protocol Section 9.3.1 Participants Who Become Pregnant During the Trial"
                                        }
                                    ],
                                    "text": "section9.3.1-pregnant"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify: • the assessments to be performed • type and duration of monitoring • whether participants who become pregnant during the trial may continue with trial intervention or must be discontinued from trial intervention (refer to Section 7 Participant Discontinuation of Trial Intervention and Discontinuation or Withdrawal from Trial as applicable) • any trial modifications that need to be made for participants who become pregnant • what information will be collected about a participant who becomes pregnant during the trial (e.g., recording and reporting to the Sponsor, postpartum follow-up, trial intervention discontinuation or continuation, or trial withdrawal). For postpartum follow-up, include the time period (e.g., initial child development) with the justification. If exposure to trial intervention during breastfeeding is applicable, specify: • the assessments to be performed • type and duration of monitoring • what information will be collected for both the participant and child]</div>"
                                }
                            },
                            {
                                "title": "9.3.2 Participants Whose Partners Become Pregnant During the Trial",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218623",
                                            "display": "ICH M11 Protocol Section 9.3.2 Participants Whose Partners Become Pregnant During the Trial"
                                        }
                                    ],
                                    "text": "section9.3.2-pregnant-partner"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify: • whether the investigator will attempt to collect pregnancy information about a participant’s partner who becomes pregnant during the specified period in the trial • whether the participant whose partner becomes pregnant should be discontinued from trial intervention (refer to Section 7 Participant Discontinuation of Trial Intervention and Discontinuation or Withdrawal from Trial as applicable) • the assessments to be performed, type and duration of monitoring, and the information to be collected]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "9.4 Special Safety Situations",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218624",
                                    "display": "ICH M11 Protocol Section 9.4 Special Safety Situations"
                                }
                            ],
                            "text": "section9.4-special-safety-situations"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify special safety situations associated with the trial intervention(s) that do not qualify as an AE or SAE, but require regulatory reporting. Examples include: • misuse or abuse • off-label use (if applicable) • medication error (prescription or dispensing error) • occupational exposure • use outside of what is foreseen in the protocol • unintended exposure of embryo, foetus, or child via maternal exposure (pregnancy or breastfeeding) or via paternal exposure (semen) • lack of therapeutic efficacy; this is not applicable for studies that measure efficacy as a study endpoint • suspected transmission of an infectious agent; this is only applicable for injected or biologic medicinal products • product complaint, including falsified or counterfeit products • suspected drug-food or drug-drug interaction]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection10Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-10"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218625",
                        "display": "ICH M11 Protocol Section 10 STATISTICAL CONSIDERATIONS"
                    }
                ],
                "text": "section10-statistics"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 10 (Statistical Considerations) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "10 STATISTICAL CONSIDERATIONS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218625",
                            "display": "ICH M11 Protocol Section 10 STATISTICAL CONSIDERATIONS"
                        }
                    ],
                    "text": "section10-statistics"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Ensure that the data analysis complies with ICH E9 Guideline and ICH E9(R1) Guideline. In general, all relevant data collected in the trial should be considered in this section. No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "10.1 General Considerations",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218626",
                                    "display": "ICH M11 Protocol Section 10.1 General Considerations"
                                }
                            ],
                            "text": "section10.1-general-considerations"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide general statements related to statistical considerations, such as whether a separate statistical analysis plan exists, which summary statistics will be provided, and the timing of analyses (e.g., “The analysis will include all participant data at trial completion”).]</div>"
                        }
                    },
                    {
                        "title": "10.2 Analysis Sets",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218627",
                                    "display": "ICH M11 Protocol Section 10.2 Analysis Sets"
                                }
                            ],
                            "text": "section10.2-analysis-sets"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe analysis sets to be considered at the trial level, i.e., the set of participants whose data are to be included in the analyses, aligned with estimands. Clearly specify the analysis set to be used for each analysis described in Section 10 Statistical Considerations.]</div>"
                        }
                    },
                    {
                        "title": "10.3 Analyses of Demographics and Other Baseline Variables",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218628",
                                    "display": "ICH M11 Protocol Section 10.3 Analyses of Demographics and Other Baseline Variables"
                                }
                            ],
                            "text": "section10.3-analyses-demographics"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the summary statistics that will be used to characterize the distribution of demographic and other relevant variables at baseline. Specify when the variables are measured (e.g., at trial inclusion, prior to randomisation, or at the time of randomisation). Relevant variables include but are not limited to: stratification variables specified in Section 6.7 Investigational Trial Intervention Assignment, Randomisation and Blinding, covariates for the statistical models specified in Section 10.4 Analyses Associated with the Primary Objective(s), other suspected predictive or prognostic variables, and variables used for planned subgroup analyses.]</div>"
                        }
                    },
                    {
                        "title": "10.4 Analyses Associated with the Primary Objective(s)",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218629",
                                    "display": "ICH M11 Protocol Section 10.4 Analyses Associated with the Primary Objective(s)"
                                }
                            ],
                            "text": "section10.4-analysis-primary-objective"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No content here. Create a new section for each estimand.]</div>"
                        },
                        "section": [{
                            "title": "10.4.1 Primary Objective <#>",
                            "code": {
                                "coding": [
                                    {
                                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                        "code": "C218630",
                                        "display": "ICH M11 Protocol Section 10.4.1 Primary Objective"
                                    }
                                ],
                                "text": "section10.4.1-analysis-primary-objective-instance"
                            },
                            "focus": {
                                "type": "EvidenceVariable",
                                "display": "[Replace with VariableDefinition Profile of EvidenceVariable Resource.]"
                            },
                            "text": {
                                "status": "empty",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                            },
                            "section": [
                                {
                                    "title": "10.4.1.1 Statistical Analysis Method",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218631",
                                                "display": "ICH M11 Protocol Section 10.4.1.1 Statistical Analysis Method"
                                            }
                                        ],
                                        "text": "section10.4.1.1-statistical-method"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the statistical analysis methods that will be used to evaluate the primary objective(s) and associated estimand(s) in Section 3.1 Primary Objective(s) and Associated Estimands. Ensure that the statistical hypothesis/model/analysis (and corresponding assumptions) is aligned with the primary estimand(s). For each objective, when applicable, state the null and alternative hypotheses, including the pre-planned type 1 error rate, or alternative criteria for evaluating whether the objective has been met, and relevant operating characteristics if appropriate. Describe the statistical model used and the factors that will be included (covariates and interactions) and any rules for handling these factors (e.g., pooling of centres). If modelling and simulation methods are to be used, describe the model (inputs and outputs), the underlying assumptions, and the method of model fitting. Entries should Reference EndpointAnalysisPlan Profile of Evidence Resource.]</div>"
                                    }
                                },
                                {
                                    "title": "10.4.1.2 Handling of Data in Relation to Primary Estimand(s)",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218632",
                                                "display": "ICH M11 Protocol Section 10.4.1.2 Handling of Data in Relation to Primary Estimand(s)"
                                            }
                                        ],
                                        "text": "section10.4.1.2-data-handling"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[For each intercurrent event of the primary estimand(s) (Section 3.1 Primary Objective(s) and Associated Estimands), explain how data will be handled for the statistical analysis in line with the primary estimand. The handling of intercurrent events in the statistical analysis should be aligned with the specific estimand strategies being used. This section should describe in more detail the rationale and handling of the data rather than repeating information from the preceding sections.]</div>"
                                    }
                                },
                                {
                                    "title": "10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s)",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218633",
                                                "display": "ICH M11 Protocol Section 10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s)"
                                            }
                                        ],
                                        "text": "section10.4.1.3-missing-data-handling"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe how missing data will be addressed (e.g., imputation method and model), state the underlying assumptions, and provide a rationale for the approach.]</div>"
                                    }
                                },
                                {
                                    "title": "10.4.1.4 Sensitivity Analysis",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218634",
                                                "display": "ICH M11 Protocol Section 10.4.1.4 Sensitivity Analysis"
                                            }
                                        ],
                                        "text": "section10.4.1.4-sensitivity-analysis"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any sensitivity analyses and how their assumptions changed from the assumptions of the main statistical analysis. Sensitivity analyses are a series of analyses conducted with the intent to explore the robustness of inferences from the main estimator to deviations from its underlying modelling assumptions and limitations in the data. Entries should Reference EndpointAnalysisPlan Profile of Evidence Resource.]</div>"
                                    }
                                },
                                {
                                    "title": "10.4.1.5 Supplementary Analysis",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218635",
                                                "display": "ICH M11 Protocol Section 10.4.1.5 Supplementary Analysis"
                                            }
                                        ],
                                        "text": "section10.4.1.5-supplementary-analysis"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any supplementary analysis, if applicable. Supplementary analyses are conducted in addition to the main and sensitivity analysis with the intent to provide additional insights into the understanding of the treatment effect.]</div>"
                                    }
                                }
                            ]
                        }]
                    },
                    {
                        "title": "10.5 Analyses Associated with the Secondary Objective(s)",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218636",
                                    "display": "ICH M11 Protocol Section 10.5 Analyses Associated with the Secondary Objective(s)"
                                }
                            ],
                            "text": "section10.5-analysis-secondary-objective"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No content here. Create a new section for each estimand. Unless there is no secondary objective, in which case indicate 'Not applicable.']</div>"
                        },
                        "section": [{
                            "title": "10.5.1 Secondary Objective <#>",
                            "code": {
                                "coding": [
                                    {
                                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                        "code": "C218637",
                                        "display": "ICH M11 Protocol Section 10.5.1 Secondary Objective"
                                    }
                                ],
                                "text": "section10.5.1-analysis-secondary-objective-instance"
                            },
                            "focus": {
                                "type": "EvidenceVariable",
                                "display": "[Replace with VariableDefinition Profile of EvidenceVariable Resource.]"
                            },
                            "text": {
                                "status": "empty",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                            },
                            "section": [
                                {
                                    "title": "10.5.1.1 Statistical Analysis Method",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218638",
                                                "display": "ICH M11 Protocol Section 10.5.1.1 Statistical Analysis Method"
                                            }
                                        ],
                                        "text": "section10.5.1.1-statistical-method"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Clearly specify any secondary hypotheses that will be tested for confirmatory purposes. Entries should Reference EndpointAnalysisPlan Profile of Evidence Resource.]</div>"
                                    }
                                },
                                {
                                    "title": "10.5.1.2 Handling of Data in Relation to Secondary Estimand(s)",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218639",
                                                "display": "ICH M11 Protocol Section 10.5.1.2 Handling of Data in Relation to Secondary Estimand(s)"
                                            }
                                        ],
                                        "text": "section10.5.1.2-data-handling"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Enter Handling of Data in Relation to Secondary Estimand(s)]</div>"
                                    }
                                },
                                {
                                    "title": "10.5.1.3 Handling of Missing Data in Relation to Secondary Estimand(s)",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218640",
                                                "display": "ICH M11 Protocol Section 10.5.1.3 Handling of Missing Data in Relation to Secondary Estimand(s)"
                                            }
                                        ],
                                        "text": "section10.5.1.3-missing-data-handling"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe how missing data will be addressed (e.g., imputation method and model), state the underlying assumptions, and provide a rationale for the approach. Refer to the E9(R1) addendum when the estimand framework is used.]</div>"
                                    }
                                },
                                {
                                    "title": "10.5.1.4 Sensitivity Analysis",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218641",
                                                "display": "ICH M11 Protocol Section 10.5.1.4 Sensitivity Analysis"
                                            }
                                        ],
                                        "text": "section10.5.1.4-sensitivity-analysis"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe sensitivity analyses. Sensitivity analyses are a series of analyses conducted with the intent to explore the robustness of inferences from the main estimator to deviations from its underlying modelling assumptions and limitations in the data. Entries should Reference EndpointAnalysisPlan Profile of Evidence Resource.]</div>"
                                    }
                                },
                                {
                                    "title": "10.5.1.5 Supplementary Analysis",
                                    "code": {
                                        "coding": [
                                            {
                                                "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                                "code": "C218642",
                                                "display": "ICH M11 Protocol Section 10.5.1.5 Supplementary Analysis"
                                            }
                                        ],
                                        "text": "section10.5.1.5-supplementary-analysis"
                                    },
                                    "text": {
                                        "status": "empty",
                                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any supplementary analysis if applicable. Supplementary analyses are conducted in addition to the main and sensitivity analysis with the intent to provide additional insights into the understanding of the treatment effect.]</div>"
                                    }
                                }
                            ]
                        }]
                    },
                    {
                        "title": "10.6 Analyses Associated with the Exploratory Objective(s)",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218643",
                                    "display": "ICH M11 Protocol Section 10.6 Analyses Associated with the Exploratory Objective(s)"
                                }
                            ],
                            "text": "section10.6-analysis-exploratory-objective"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any exploratory analyses, if applicable. Additional subsections may be created to describe the analyses for each exploratory objective, as needed. If there is no exploratory objective, indicate “Not applicable”.]</div>"
                        }
                    },
                    {
                        "title": "10.7 Safety Analyses",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218644",
                                    "display": "ICH M11 Protocol Section 10.7 Safety Analyses"
                                }
                            ],
                            "text": "section10.7-safety-analyses"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If safety is a primary and/or secondary objective, describe the corresponding safety analyses in the appropriate section above (Section 10.4 Analyses Associated with the Primary Objective(s) or Section 10.5 Analyses Associated with the Secondary Objective[s]). In this section, describe statistical methods that will be used to analyse relevant safety outcomes, including any AESI. This should typically include specification of a measure to estimate risk within treatment arms, a measure to compare risks across treatment arms, and a measure of statistical uncertainty around the comparison such as a confidence interval.]</div>"
                        }
                    },
                    {
                        "title": "10.8 Other Analyses",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218645",
                                    "display": "ICH M11 Protocol Section 10.8 Other Analyses"
                                }
                            ],
                            "text": "section10.8-other-analyses"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe other analyses not included in Sections 10.3-10.7, such as subgroup analyses.]</div>"
                        }
                    },
                    {
                        "title": "10.9 Interim Analyses",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218646",
                                    "display": "ICH M11 Protocol Section 10.9 Interim Analyses"
                                }
                            ],
                            "text": "section10.9-interim-analyses"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe any interim analyses and criteria for stopping or adapting the trial. Ensure alignment with Section 4.3 Trial Stopping Rules. The description should include, but is not limited to, the following. Under circumstances where interim analysis details could impede the integrity of the trial, some of the information can be added in other documents outside of the protocol. • any planned interim analysis, even if it is only to be performed at the request of an oversight body (for example, DMC) • the purpose of the interim analysis, including whether the interim analysis may be used for stopping and/or for other trial adaptations such as sample size re-estimation, alteration to the proportion of participants allocated to each trial group, or changes to eligibility criteria • the applied statistical method; e.g., group sequential test and spending function (e.g., O’Brien-Fleming), as applicable • the parties responsible for performing and reviewing the results of the analyses (e.g., DMC, independent statistician) • when the analyses will be conducted (timing and/or triggers) • the decision criteria—statistical or other—that will be adopted to judge the interim results as part of a guideline for early stopping or other adaptations • who will see the outcome data while the trial is ongoing • whether these individuals will remain blinded to trial groups • how the integrity of the trial implementation will be protected (e.g., maintaining blinding) when decisions are made after interim analyses (e.g., a decision to continue the trial or implement a specific adaptation)]</div>"
                        }
                    },
                    {
                        "title": "10.10 Multiplicity Adjustments",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218647",
                                    "display": "ICH M11 Protocol Section 10.10 Multiplicity Adjustments"
                                }
                            ],
                            "text": "section10.10-multiplicity-adjustments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Multiple testing procedures may be needed to limit the probability of false positive findings in a trial. Reasons for carrying out multiple statistical tests include - but are not restricted to - multiple endpoints, multiple treatment groups, multiple hypotheses, subgroups, multiple timepoints. Describe any approaches to multiplicity control for the trial. This description might go beyond the analysis of primary objectives. Specify the statistical approach to control the overall type I error rate as well as the (adjusted) significance levels to test specific hypotheses, as applicable. Clarify whether the tests/confidence intervals are one- or two-sided. State the circumstances under which a trial will be considered to have met its primary objective(s). For example, in a study with two primary efficacy endpoints, this section should state whether the study would be expected to provide statistical evidence on at least one or on both of the endpoints in order to confirm the efficacy of the treatment. For some statistical approaches it might be helpful to include a graphical depiction, as visualisation will be helpful for understanding, coupled with the clinical translation of the mathematical choices. Details regarding interim analyses should be provided in Section 10.9 Interim Analyses.]</div>"
                        }
                    },
                    {
                        "title": "10.11 Sample Size Determination",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218648",
                                    "display": "ICH M11 Protocol Section 10.11 Sample Size Determination"
                                }
                            ],
                            "text": "section10.11-sample-size-determination"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[This section should detail the methods used for the determination of the sample size. The sample size calculation should be aligned with the primary estimand and the primary analysis, otherwise a justification is needed. Details of sample size calculation should include all relevant information to enable reproduction of the sample size, e.g.,: • referencing any prior studies on which assumptions were based • significance level (including information on the choice of one- or two-sided level) • power • assumed treatment effect and variability • how dropout rate and intercurrent events have been incorporated into sample size calculation • precision of estimator/length of confidence interval. Any assumptions made should be stated and justified. Further analysis of how deviations from the assumptions will affect the sample size should be included. If complex simulations were used to calculate the sample size, consider including details in a separate simulation report as an appendix to the protocol. If the planned sample size is not derived statistically, then this should be explicitly stated along with a rationale for the intended sample size (e.g., exploratory nature of pilot trials; pragmatic considerations for trials in rare diseases). Entries should Reference EndpointAnalysisPlan Profile of Evidence Resource.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection11Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-11"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218649",
                        "display": "ICH M11 Protocol Section 11 TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS"
                    }
                ],
                "text": "section11-oversight"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 11 (Trial Oversight) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "11 TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218649",
                            "display": "ICH M11 Protocol Section 11 TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS"
                        }
                    ],
                    "text": "section11-oversight"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "11.1 Regulatory and Ethical Considerations",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218650",
                                    "display": "ICH M11 Protocol Section 11.1 Regulatory and Ethical Considerations"
                                }
                            ],
                            "text": "section11.1-regulatory-considerations"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Provide a high-level statement on the prevailing ethical, legal, and regulatory guidelines that will be applied throughout the trial. For example, This trial will be conducted in accordance with the protocol and with the following: • Ethical principles that have their origin in the Declaration of Helsinki for medical research involving human subjects • Consensus ethical principles derived from international guidelines including the Declaration of Helsinki and the Council for International Organisations of Medical Sciences (CIOMS) International Ethical Guidelines • ICH Good Clinical Practice (GCP) Guidelines • Applicable laws and regulations]</div>"
                        }
                    },
                    {
                        "title": "11.2 Trial Oversight",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218651",
                                    "display": "ICH M11 Protocol Section 11.2 Trial Oversight"
                                }
                            ],
                            "text": "section11.2-trial-oversight"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Concisely summarize the trial oversight listing the investigator and sponsor responsibilities not covered in other sections of the protocol which are essential for the operations of the trial, specifying the ones related to quality assurance.]</div>"
                        },
                        "section": [
                            {
                                "title": "11.2.1 Investigator Responsibilities",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218652",
                                            "display": "ICH M11 Protocol Section 11.2.1 Investigator Responsibilities"
                                        }
                                    ],
                                    "text": "section11.2.1-investigator-responsibilities"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the investigator duties, including the oversight of duties delegated to a third party that may impact the trial conduct at sites, if applicable and if not addressed elsewhere.]</div>"
                                }
                            },
                            {
                                "title": "11.2.2 Sponsor Responsibilities",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218653",
                                            "display": "ICH M11 Protocol Section 11.2.2 Sponsor Responsibilities"
                                        }
                                    ],
                                    "text": "section11.2.2-sponsor-responsibilities"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the sponsor duties, including those to be transferred to a third party that may impact the investigators sites, if applicable.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "11.3 Informed Consent Process",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218654",
                                    "display": "ICH M11 Protocol Section 11.3 Informed Consent Process"
                                }
                            ],
                            "text": "section11.3-informed-consent-process"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify the key elements of the informed consent process, including any special needs and how these are addressed (for example, assent, capacity, legally acceptable representative, adolescents who may reach age of majority during the trial, pregnant participants and pregnant partners of participants).]</div>"
                        },
                        "section": [
                            {
                                "title": "Assent Process",
                                "code": {
                                    "text": "section11.3.0-assent-process"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Enter Description of Assent Process.]</div>"
                                }
                            },
                            {
                                "title": "Emergency Consent Process",
                                "code": {
                                    "text": "section11.3.0-emergency-consent-process"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If enrollment in the trial may occur during an emergency in which the participant or their legally acceptable representative is not able or available to give consent, describe the consent process.]</div>"
                                }
                            },
                            {
                                "title": "11.3.1 Informed Consent for Rescreening",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218655",
                                            "display": "ICH M11 Protocol Section 11.3.1 Informed Consent for Rescreening"
                                        }
                                    ],
                                    "text": "section11.3.1-rescreening-consent-process"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If participants can be rescreened as described in Section 5.6, state whether the participant needs to complete a new consent. Screen failure and rescreening should be clearly defined in the protocol, with cross reference to those definitions.]</div>"
                                }
                            },
                            {
                                "title": "11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research",
                                "code": {
                                    "coding": [
                                        {
                                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                            "code": "C218656",
                                            "display": "ICH M11 Protocol Section 11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research"
                                        }
                                    ],
                                    "text": "section11.3.2-remaining-samples"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[If participants will be asked to consent to optional exploratory research using the remainder of mandatory samples, describe the use of remaining samples for optional exploratory research. If any exploratory research is planned and additional written consent regarding the use of remaining samples for exploratory research will be obtained, describe the consent process.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "11.4 Committees",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218657",
                                    "display": "ICH M11 Protocol Section 11.4 Committees"
                                }
                            ],
                            "text": "section11.4-committees"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Briefly describe the administrative structure of committees that will be reviewing data while the trial is ongoing, and the type of committee (e.g., Dose Escalation Committee, Data Monitoring Committee or Data Safety Monitoring Board). Note that specific details may be required depending on local law or regulation. If applicable, Committee Charters may be cross referenced. If no committees are involved, state “Not applicable.”]</div>"
                        }
                    },
                    {
                        "title": "11.5 Insurance and Indemnity",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218658",
                                    "display": "ICH M11 Protocol Section 11.5 Insurance and Indemnity"
                                }
                            ],
                            "text": "section11.5-insurance-and-indemnity"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Concisely summarize the arrangements for participants insurance and indemnity if not addressed in a separate agreement, if required by the applicable regulatory requirements.]</div>"
                        }
                    },
                    {
                        "title": "11.6 Risk-Based Quality Management",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218659",
                                    "display": "ICH M11 Protocol Section 11.6 Risk-Based Quality Management"
                                }
                            ],
                            "text": "section11.6-risk-management"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the identified critical to quality factors, associated risks and risk mitigation strategies in the trial or refer to a separate document where this is described.]</div>"
                        }
                    },
                    {
                        "title": "11.7 Data Governance",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218660",
                                    "display": "ICH M11 Protocol Section 11.7 Data Governance"
                                }
                            ],
                            "text": "section11.7-data-governance"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the key systems and processes for critical trial integrity, traceability and security including a summary of the monitoring approaches enabling accurate collection, reporting, monitoring, transfer, retention, and access if not addressed in separate agreement(s).]</div>"
                        }
                    },
                    {
                        "title": "11.8 Data Protection",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218661",
                                    "display": "ICH M11 Protocol Section 11.8 Data Protection"
                                }
                            ],
                            "text": "section11.8-data-protection"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe the measures to protect the privacy and confidentiality of personal information of trial participants in accordance with applicable regulatory requirements on personal data protection and any measures that should be taken in case of a data security breach.]</div>"
                        }
                    },
                    {
                        "title": "11.9 Source Records",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218662",
                                    "display": "ICH M11 Protocol Section 11.9 Source Records"
                                }
                            ],
                            "text": "section11.9-source-data"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[State the importance of source records and expectation for traceability. Delineate expectations for investigators (e.g., maintain and ensure availability of essential records) and trial monitors (e.g., ensure participant protections, ensure that the trial is conducted according to GCP). Identify what constitutes source records and its origin or provide a reference to the location of this information, if contained in a separate document. Describe the provision for direct access to source records enabling clinical trial-related monitoring, audits and regulatory inspections, if not included in separate agreement(s).]</div>"
                        }
                    },
                    {
                        "title": "11.10 Protocol Deviations",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218663",
                                    "display": "ICH M11 Protocol Section 11.10 Protocol Deviations"
                                }
                            ],
                            "text": "section11.10-protocol-deviations"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe plans for detecting, reviewing, and reporting any deviations from the protocol or include reference to a separate document.]</div>"
                        }
                    },
                    {
                        "title": "11.11 Early Site Closure",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218664",
                                    "display": "ICH M11 Protocol Section 11.11 Early Site Closure"
                                }
                            ],
                            "text": "section11.11-early-site-closure"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                        },
                        "section": [
                            {
                                "title": "Decision Rights for Site Closure",
                                "code": {
                                    "text": "section11.11-early-site-closure#decisionRights"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[List the sponsor’s rights to close a site early. Likewise, list the investigator’s rights to initiate early site closure.]</div>"
                                }
                            },
                            {
                                "title": "Crteria for Early Closure",
                                "code": {
                                    "text": "section11.11-early-site-closure#criteria"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[List the criteria for early closure of a site by the sponsor or investigator.]</div>"
                                }
                            },
                            {
                                "title": "Responsibilities Following Early Site Closure",
                                "code": {
                                    "text": "section11.11-early-site-closure#responsibilities"
                                },
                                "text": {
                                    "status": "empty",
                                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[List the responsibilities of the sponsor and investigator following early site closure, such as informing the ethics committee(s), and prompt notification of the participant and their transition to appropriate therapy and/or follow-up.]</div>"
                                }
                            }
                        ]
                    },
                    {
                        "title": "11.12 Data Dissemination",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218665",
                                    "display": "ICH M11 Protocol Section 11.12 Data Dissemination"
                                }
                            ],
                            "text": "section11.12-data-dissemination"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Describe whether the clinical trial will be registered in public databases, including reporting of results, if applicable.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection12Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-12"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218666",
                        "display": "ICH M11 Protocol Section 12 APPENDIX: SUPPORTING DETAILS"
                    }
                ],
                "text": "section12-supporting-details"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 12 (Supporting Details) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "12 APPENDIX: SUPPORTING DETAILS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218666",
                            "display": "ICH M11 Protocol Section 12 APPENDIX: SUPPORTING DETAILS"
                        }
                    ],
                    "text": "section12-supporting-details"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No text is intended here (heading only).]</div>"
                },
                "section": [
                    {
                        "title": "12.1 Clinical Laboratory Tests",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218667",
                                    "display": "ICH M11 Protocol Section 12.1 Clinical Laboratory Tests"
                                }
                            ],
                            "text": "section12.1-clinical-laboratory-tests"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Specify which laboratory parameters should be included in each clinical laboratory assessment panel (for example, for haematology, chemistry, urinalysis). A tabular presentation for such information is common. If applicable, include equations and references for locally calculated laboratory results. If not applicable, retain header and enter “Not Applicable.”]</div>"
                        }
                    },
                    {
                        "title": "12.2 Country/Region-Specific Differences",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218668",
                                    "display": "ICH M11 Protocol Section 12.2 Country/Region-Specific Differences"
                                }
                            ],
                            "text": "section12.2-country-specific-differences"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Although global clinical trial practices are increasingly harmonised, some country/ region-specific differences in requirements do exist (for example, document retention periods, contraception requirements).  Where differences in requirements cannot be reconciled, sponsors should explain how they will document and communicate country/region-specific differences (for example, by country/region-specific amendments or addenda). An alternative to country/region-specific amendments is to list the specific differences by country or countries in this section, including a reference to the relevant section of the protocol where the differing requirement applies. If not applicable, retain header and enter “Not Applicable.”]</div>"
                        }
                    },
                    {
                        "title": "12.3 Prior Protocol Amendment(s)",
                        "code": {
                            "coding": [
                                {
                                    "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                                    "code": "C218669",
                                    "display": "ICH M11 Protocol Section 12.3 Prior Protocol Amendment(s)"
                                }
                            ],
                            "text": "section12.3-prior-protocol-amendments"
                        },
                        "text": {
                            "status": "empty",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[See M11 for specific guidance if not able to simply state {Not applicable.  This protocol has not been amended.} or {Not applicable.  This is the first protocol amendment.}.]</div>"
                        }
                    }
                ]
            }
        ]
    };
    let m11ReportSection13Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-13"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218670",
                        "display": "ICH M11 Protocol Section 13 APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS"
                    }
                ],
                "text": "section13-glossary"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 13 (Glossary) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "13 APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218670",
                            "display": "ICH M11 Protocol Section :13 APPENDIX GLOSSARY OF TERMS AND ABBREVIATIONS"
                        }
                    ],
                    "text": "section13-glossary"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[Define abbreviations and other terms used in the protocol.  A tabular presentation is common and may serve as the definition at first use. Entries should Reference ValueSet Resource or CodeSystem Resource.]</div>"
                }
            }
        ]
    };
    let m11ReportSection14Json = {
        "resourceType": "Composition",
        "meta": {
            "profile": [
                "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-report-section-14"
            ]
        },
        "status": "final",
        "type": {
            "coding": [
                {
                    "system": "http://loinc.org",
                    "code": "35528-9",
                    "display": "Clinical trial protocol Clinical trial protocol"
                }
            ],
            "text": "CeSHarP Report"
        },
        "category": [
            {
                "coding": [
                    {
                        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                        "code": "C218671",
                        "display": "ICH M11 Protocol Section 14 APPENDIX: REFERENCES"
                    }
                ],
                "text": "section14-references"
            }
        ],
        "subject": [
            {
                "reference": "ResearchStudy/" + researchStudyJson.id,
                "type": "ResearchStudy",
                "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
            }
        ],
        "date": (new Date()).toISOString(),
        "title": "M11 Report Section 14 (References) for " + (researchStudyJson.title.replace(" - M11ResearchStudy", "") || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id),
        "custodian": {
            "reference": "Organization/118079",
            "type": "Organization",
            "display": "Computable Publishing LLC"
        },
        "relatesTo": [
            {
                "type": {
                    "coding": [
                        {
                            "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                            "code": "derived-from",
                            "display": "Derived from"
                        }
                    ]
                },
                "targetReference": {
                    "reference": "ResearchStudy/" + researchStudyJson.id,
                    "type": "ResearchStudy",
                    "display": researchStudyJson.title || researchStudyJson.name || "ResearchStudy FOI " + researchStudyJson.id
                }
            }
        ],
        "section": [
            {
                "title": "14 APPENDIX: REFERENCES",
                "code": {
                    "coding": [
                        {
                            "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
                            "code": "C218671",
                            "display": "ICH M11 Protocol Section 14 APPENDIX: REFERENCES"
                        }
                    ],
                    "text": "section14-references"
                },
                "text": {
                    "status": "empty",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">[References should be listed in a common format that includes all relevant information to identify the source and date published.  If not published, this should be clearly indicated. Entries should Reference Citation Resource.]</div>"
                }
            }
        ]
    };

    let m11ReportJsonDictionary = {
        m11ReportContainerJson: m11ReportContainerJson,
        m11ReportSection01Json: m11ReportSection01Json,
        m11ReportSection02Json: m11ReportSection02Json,
        m11ReportSection03Json: m11ReportSection03Json,
        m11ReportSection04Json: m11ReportSection04Json,
        m11ReportSection05Json: m11ReportSection05Json,
        m11ReportSection06Json: m11ReportSection06Json,
        m11ReportSection07Json: m11ReportSection07Json,
        m11ReportSection08Json: m11ReportSection08Json,
        m11ReportSection09Json: m11ReportSection09Json,
        m11ReportSection10Json: m11ReportSection10Json,
        m11ReportSection11Json: m11ReportSection11Json,
        m11ReportSection12Json: m11ReportSection12Json,
        m11ReportSection13Json: m11ReportSection13Json,
        m11ReportSection14Json: m11ReportSection14Json
    }

    return m11ReportJsonDictionary;
};

export default generateM11ReportComposition;
export { narrativeStringForCharacteristic };